import { Children, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../lib/idioma.js';
import { variaveisDoCartao, type CartaoGravado } from '@vestigo/core/area-cartao-da-prateleira';
import { Link } from 'react-router-dom';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';
import {
  aspectoDaCapa,
  larguraDaCapa,
  SIZES_DO_HEROI,
  sizesDaCapa,
  type TamanhoDeCapa,
} from '@vestigo/core/area-largura-da-capa';
import type {
  CursoDaVitrine,
  ItemDaPrateleira,
  SecaoDaVitrine,
  SlideDoHeroi,
  Vitrine as DadosDaVitrine,
} from '../lib/api.js';

/**
 * O item de trilha numa prateleira, como a rota o serve.
 *
 * Recortado da união em vez de escrito de novo: o dia em que o servidor
 * acrescentar um campo, ele chega aqui por existir — e não por alguém se
 * lembrar de copiar a declaração para o segundo sítio.
 */
type ItemDeTrilha = Extract<ItemDaPrateleira, { tipo: 'trilha' }>;
import { secoesDaVitrine } from '@vestigo/core/area-vitrine-layout';
import {
  aspectoDoHeroi,
  encaixeDoSlide,
  heroiCompacto,
  minimoDoHeroi,
} from '@vestigo/core/area-heroi-geometria';
import { estiloDoCorte, type Corte } from '../lib/corte-da-capa.js';
import {
  devolverAVez,
  enderecoDaPrevia,
  lerAmbienteDoMovimento,
  pedirAVez,
  podeAnimar,
} from '../lib/capa-animada.js';
import { cargaPorExtenso, duracaoLegivel, posicaoLegivel } from '../lib/tema.js';
/* `duracao` do core, e não um formatador desta pasta: é a mesma função que
   imprime a carga na sala e na frase que o tutor lê no painel. Ver
   `legendaDoCurso`. */
import { duracao } from '@vestigo/core/area-prateleira-itens';
import { rotuloDoTipo } from '../lib/tipo-de-aula.js';
import { marcaDoCurso, Monograma } from './MarcaDoCurso.js';
import { caminhoDaArea } from '../lib/endereco.js';
import { lerTranca, type TrancaDeOrdem } from '../lib/tranca.js';
import { lerLancamento, type Lancamento } from '../lib/lancamento.js';
import {
  AvisoDaTranca,
  Cadeado,
  MotivoDaTranca,
  MotivoNoCartaz,
  SeloDeOrdem,
  VeuDoCartaz,
} from './TrancaDeOrdem.js';
import { EstadoNoCartaz, SeloDeLancamento } from './Lancamento.js';
/* O cartaz que só existe na bancada (04/11/2026) — ver `SoVoceVe.tsx`. */
import { SeloSoVoceVe, SoVoceVeNoCartaz, SoVoceVeNoHeroi } from './SoVoceVe.js';
import { SeloDeAmostra, SeloSemAcesso, SetaQueSai } from './Oferta.js';
import type { Tradutor } from '@vestigo/core/i18n-da-tela';

/**
 * ── O GLIFO DO DESTINO DE DENTRO (`0461`) ───────────────────────────────────
 *
 * A irmã da `SetaQueSai` (em `Oferta.js`), para o cartaz cujo destino é uma rota
 * DESTE app — a página de promoção que o Vestigo serve quando o tutor não
 * declarou a dele.
 *
 * Uma seta que **avança**, e não uma que sai pela diagonal: a diagonal é o
 * desenho de `target="_blank"`, e usá-la aqui prometeria uma aba nova que não
 * acontece. Mora neste ficheiro, e não em `Oferta.tsx`, porque quem a usa é só o
 * cartaz da vitrine — e aquele ficheiro é da tela de DENTRO, escrita na mesma
 * tarde por outra sessão.
 */
function SetaQueLeva({ tamanho = 18 }: { tamanho?: number }) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M5 9h8" />
      <path d="M9.5 5.5 13 9l-3.5 3.5" />
    </svg>
  );
}

/**
 * A vitrine, no formato que todo mundo já sabe usar.
 *
 * Prateleiras horizontais com rolagem por toque e `scroll-snap`, capas em
 * cartaz, progresso na base do card. A escolha do formato não é estética: é o
 * que dispensa explicação — ninguém precisa aprender a navegar aqui.
 */

/* A proporção sai de `aspectoDaCapa`, no core. Aqui havia uma segunda cópia da
 * lista — e enquanto ela não mudava, as duas concordavam. Ao entrar o `3/4`, que
 * é o padrão novo, esta cópia não o conhecia: o cartaz recebia a LARGURA do
 * `3/4` (que vinha de `larguraDaCapa`, importada) e a ALTURA do `2/3`, pelo
 * `??` de segurança. Ninguém reporta isso, porque parece só um pouco errado. */

export function Vitrine({
  dados,
  cursos,
  prateleirasMesmoVazias = false,
}: {
  /** A bancada (hotfix da leva F): uma prateleira nova, sem cartão, tem de aparecer — é onde se solta o primeiro. */
  prateleirasMesmoVazias?: boolean;
  /*
   * `Omit<…, 'cursos'>` de propósito: quem entrega os cursos é o `Map` ao lado,
   * e não este objeto. A vitrine do aluno os recebe do próprio corpo da
   * resposta; a Prova do Estúdio os recebe num campo separado do painel. Exigir
   * o array aqui obrigaria a Prova a inventar um campo que ela não tem, só para
   * o componente ignorá-lo.
   */
  dados: Omit<DadosDaVitrine, 'cursos'>;
  cursos: Map<string, CursoDaVitrine>;
  /*
   * `trilhasMesmoVazia` saiu daqui em 06/10/2026, com a seção que ele desenhava.
   * Ele existia para a Prova do Estúdio ter a região `trilhas` no DOM mesmo sem
   * trilha nenhuma — era por ela que se criava a primeira. A seção já não
   * existe; a trilha é um item de PRATELEIRA, e a porta dela na Prova é o cartaz
   * (`data-vst-regiao="trilha"`) ou a faixa vazia, que `prateleirasMesmoVazias`
   * continua a desenhar.
   */
}) {
  const t = useT();
  const heroiCurso = dados.heroi?.curso ? cursos.get(dados.heroi.curso) : undefined;
  /* Os slides já vêm resolvidos do servidor (leva A3); um corpo antigo deriva
     um slide do herói de sempre. Zero slides = sem herói. */
  const slides: SlideDoHeroi[] = dados.heroi
    ? (dados.heroi.slides ?? slideLegado(dados.heroi, heroiCurso))
    : [];
  const temHeroi = slides.length > 0;
  /*
   * ── A FAIXA RESPONDE POR DUAS COISAS DESDE 30/09/2026 ────────────────────
   *
   * As aulas em andamento, e a trilha em curso. Salvar uma trilha põe as aulas
   * dela na FILA e não as começa — então um aluno pode ter trilha e zero aula em
   * andamento, e sem esta segunda metade o cartão dela não teria faixa onde
   * pousar. O servidor conta-a do mesmo jeito em `presentes.retomada`, e é por
   * isso que as duas contas dizem a mesma coisa.
   */
  const aMinhaTrilha = dados.minhaTrilha ?? null;
  const temRetomada =
    Boolean(dados.continuando && dados.continuando.length > 0) || aMinhaTrilha !== null;

  /*
   * ── AS SEÇÕES, NA ORDEM QUE O DONO DISPÔS (leva A2 da bancada) ─────────────
   *
   * O servidor já resolveu a ordem e o tom (`secoes`); um corpo antigo, sem o
   * campo, cai na MESMA função com layout vazio — a ordem de hoje. É uma regra
   * só, nos dois lados, e por isso a Prova e a vitrine não divergem (C7).
   * O que a tela confere por conta própria é a PRESENÇA (há herói? há
   * retomada?), porque é ela quem tem os dados na mão.
   */
  const secoes =
    dados.secoes ??
    secoesDaVitrine([], dados.prateleiras, {
      heroi: temHeroi,
      saudacao: Boolean(dados.saudacao),
      retomada: temRetomada,
      /* `trilhas` saiu do `Record` (`0467`) — a seção deixou de existir. */
    });
  const prateleiraPorId = new Map(dados.prateleiras.map((p) => [p.id, p]));

  const desenharPrateleira = (p: DadosDaVitrine['prateleiras'][number]) => {
    /* A faixa vence a área por eixo (leva A1): o servidor já resolveu a
     * herança; um corpo antigo, sem os campos, cai na aparência da área. */
    const proporcao = p.proporcao ?? dados.capa;
    const tamanho = p.tamanho ?? dados.capaTamanho;
    /* `itens` traz curso, módulo, aula e **trilha** na ordem; um corpo sem ele
     * (a prévia de um painel antigo) continua sendo só os cursos. */
    const itens: ItemDaPrateleira[] =
      p.itens ?? p.cursos.map((id) => ({ tipo: 'curso' as const, id }));
    const cartoes = itens.flatMap((item) => {
      if (item.tipo === 'trilha') {
        return [
          <CapaDeTrilha
            key={`trilha:${item.id}`}
            item={item}
            areaSlug={dados.area.slug}
            proporcao={proporcao}
            tamanho={tamanho}
          />,
        ];
      }
      if (item.tipo === 'curso') {
        const c = cursos.get(item.id);
        return c
          ? [
              <CapaDeCurso
                key={`curso:${c.id}`}
                curso={c}
                areaSlug={dados.area.slug}
                proporcao={proporcao}
                tamanho={tamanho}
              />,
            ]
          : [];
      }
      return [
        <CapaDeItem
          key={`${item.tipo}:${item.id}`}
          item={item}
          areaSlug={dados.area.slug}
          proporcao={proporcao}
          tamanho={tamanho}
        />,
      ];
    });
    if (cartoes.length === 0 && !prateleirasMesmoVazias) return null;
    return (
      <Prateleira
        titulo={p.title}
        proporcao={proporcao}
        cartao={p.cartao as CartaoGravado | undefined}
        /* A marca só existe no Estúdio: fora dele, `data-` é HTML morto e
         * não custa um byte de JavaScript ao aluno. */
        regiao="prateleira"
        rotulo={`prateleira · ${p.title}`}
        refId={p.id}
      >
        {cartoes}
      </Prateleira>
    );
  };

  return (
    <div className="pb-16">
      {secoes.map((s, i) => {
        const anterior = secoes[i - 1];
        /* «Continue de onde parou» encosta no que vem acima quando o de cima é a
           chamada (herói ou saudação) — ver `colada`, em `Prateleira`. */
        const colada = i === 0 || anterior?.tipo === 'heroi' || anterior?.tipo === 'saudacao';

        switch (s.tipo) {
          case 'heroi':
            if (!temHeroi) return null;
            return (
              <Secao key="heroi" secao={s}>
                <Heroi dados={dados} slides={slides} cursos={cursos} />
              </Secao>
            );
          case 'saudacao':
            if (!dados.saudacao) return null;
            return (
              <Secao key="saudacao" secao={s}>
                <p
                  data-vst-regiao="saudacao"
                  /* `pt-6` e não `pt-8`: a saudação e a prateleira que vem logo abaixo
                     são a mesma frase em duas linhas — "bom te ver de volta" e o que
                     continuar. Ver `colada`, em `Prateleira`. */
                  className="px-6 pt-6 text-lg sm:px-8"
                  style={{ color: 'var(--texto-suave)' }}
                >
                  {dados.saudacao}
                </p>
              </Secao>
            );
          case 'retomada':
            /*
             * "Continue de onde parou" mostra AULAS, e não cursos.
             *
             * Era um cartão de curso, e o nome da prateleira fala de onde a pessoa
             * PAROU — que é uma aula, num minuto. Curso não se retoma: ele se abre, e
             * abrir um curso para procurar onde se estava é exatamente o trabalho que
             * esta prateleira existe para poupar. Cada cartão leva direto à aula, e o
             * servidor manda **uma por curso em andamento**, até cinco — quem escolhe
             * qual é `retomada.ts`, no core.
             *
             * CINCO é o teto da FAIXA, e não da lista de aulas: o cartão da
             * trilha em curso vem antes delas e gasta uma vaga, então com trilha
             * o servidor manda quatro aulas. Ver `tetoDeRetomadas`.
             */
            if (!temRetomada) {
              /* Na BANCADA (S5) a seção existe mesmo sem aluno com progresso: é
                 assim que ela se seleciona, se ordena e se esconde. O aluno de
                 verdade nunca a vê vazia. */
              if (!prateleirasMesmoVazias) return null;
              return (
                <Secao key="retomada" secao={s}>
                  <Prateleira
                    titulo={t('vitrine.continuar')}
                    proporcao={dados.capa}
                    colada={colada}
                    emGrade
                  >
                    <p
                      className="max-w-md px-2 text-sm leading-relaxed"
                      style={{ color: 'var(--texto-suave)' }}
                      data-vst-retomada-vazia=""
                    >
                      Aparece para o aluno que tem aulas em andamento — uma por curso, numa fileira
                      só de cinco cartões, dos quais a trilha em curso ocupa um quando existe. Aqui
                      não há aluno, então a faixa fica vazia.
                    </p>
                  </Prateleira>
                </Secao>
              );
            }
            return (
              <Secao key="retomada" secao={s}>
                <Prateleira
                  titulo={t('vitrine.continuar')}
                  proporcao={dados.capa}
                  colada={colada}
                  emGrade
                >
                  {/* A trilha vem PRIMEIRO: ela é o plano que o aluno pediu, e as
                      aulas em andamento são onde ele parou por conta própria. */}
                  {aMinhaTrilha && (
                    <CartaoDaMinhaTrilha trilha={aMinhaTrilha} areaSlug={dados.area.slug} />
                  )}
                  {(dados.continuando ?? []).map((a) => (
                    <CartaoDeRetomada key={a.lessonId} aula={a} areaSlug={dados.area.slug} />
                  ))}
                </Prateleira>
              </Secao>
            );
          /*
           * ── NÃO HÁ `case 'trilhas'`, E É O COMPILADOR QUE O GARANTE ───────
           *
           * `secoesDaVitrine` deixou de emitir a seção (`0467`, ordem do dono de
           * 06/10/2026: *«na vitrine sai o espaço especial de trilhas já que ela
           * terá sua própria prateleira»*), e o tipo que este `switch` percorre
           * é `SecaoFixaViva` — um `Exclude<…, 'trilhas'>`. Um `case 'trilhas'`
           * aqui não compila: ele seria código inalcançável a parecer um ramo.
           *
           * As trilhas vivem agora em dois sítios, os dois reais: na prateleira
           * que o tutor dispõe (`CapaDeTrilha`, abaixo) e na página própria,
           * `/a/:slug/trilhas` (`componentes/TrilhasDaArea.tsx`).
           */
          case 'prateleira': {
            const p = s.id ? prateleiraPorId.get(s.id) : undefined;
            if (!p) return null;
            const faixa = desenharPrateleira(p);
            return faixa ? (
              <Secao key={`prateleira:${p.id}`} secao={s}>
                {faixa}
              </Secao>
            ) : null;
          }
        }
      })}
    </div>
  );
}

/*
 * ── A LINHA DO ACERVO MUDOU DE CASA (0439, leva 2 — 28/09/2026) ─────────────
 *
 * Ela morava aqui, no fim da `<div className="pb-16">`, com um parágrafo a
 * explicar por que o rodapé era o «lugar discreto» que o dono tinha pedido de
 * manhã. **Na mesma tarde ele mandou o contrário** — *«coloque no topo ao lado
 * do evento da agenda»* —, e o componente foi para
 * `componentes/LinhaDoAcervo.tsx`, montado em `App.tsx` entre o cabeçalho e
 * esta vitrine.
 *
 * **Ela não voltou a ser uma seção, e isso é decisão.** As seções são o que o
 * tutor ordena e pode tirar (`secoes`, logo acima); o acervo é da moldura, não
 * do conteúdo — amarrá-lo à saudação ou a uma prateleira fá-lo-ia desaparecer
 * em parte das áreas, sem ninguém ter pedido.
 *
 * O porquê do lugar novo, com a geometria medida, está no cabeçalho do ficheiro
 * novo. Este comentário existe só para quem vier procurá-la aqui.
 */

/**
 * O invólucro de uma seção: a marca que o Estúdio lê e o TOM que o dono
 * escolheu. Tom `fundo` não emite `style` nenhum — a área que nunca mexeu
 * desenha byte a byte o que desenhava. Os três tons são variáveis do tema
 * (`--tom-*`, em `lib/tema.ts`), nomeadas uma vez; aqui só se escolhe o nome.
 */
function Secao({ secao, children }: { secao: SecaoDaVitrine; children: React.ReactNode }) {
  return (
    <div
      data-vst-secao={secao.tipo}
      data-vst-ref={secao.id ?? undefined}
      style={secao.tom === 'fundo' ? undefined : { background: `var(--tom-${secao.tom})` }}
    >
      {children}
    </div>
  );
}

/**
 * A altura mínima do herói, e a distância que o véu precisa cobrir.
 *
 * O mesmo número do `min-h-[420px]` da `<section>`, de propósito: é a altura em
 * que o véu cobre o herói **inteiro**, que é o pior caso possível. Está aqui em
 * JS porque a parada do gradiente tem de ser a mesma medida, e duas cópias
 * divergiriam na primeira vez que alguém mexesse na altura.
 */
const ALTURA_MINIMA_DO_HEROI = 420;

/**
 * O véu do herói — e por que as paradas são pixels.
 *
 * Exportado só para o teste: um gradiente com `var()` dentro não sobrevive ao
 * parser de CSS do `happy-dom` (o atributo `style` chega vazio), então a única
 * forma de trancar a regra é medindo a string antes de ela virar estilo.
 */
export function veuDoHeroi(escurecer: number): string {
  const topo = Math.max(0, escurecer - 0.28);
  return `linear-gradient(to top, var(--fundo) 8px, rgba(0,0,0,${escurecer}) ${ALTURA_MINIMA_DO_HEROI}px, rgba(0,0,0,${topo}))`;
}

/**
 * A imagem de uma caixa que já tem proporção — o que era `background-image`.
 *
 * ## Por que ela deixou de ser fundo
 *
 * Por duas razões, e as duas são C9 (§15.7).
 *
 * **A primeira é peso.** Medido em produção, baixando de verdade: a mesma imagem
 * de 848×1264 e **776 138 bytes** desenhava um cartaz de 300×450 e um herói de
 * 2033×762 — 2,8× mais pixels do que o cartaz mostra, 2,4× menos do que o herói
 * queria, e **1,9× o teto de 400 kB** nos dois. Um `background-image` não tem
 * `srcset`: ele baixa o arquivo que a URL diz, e ponto.
 *
 * **A segunda é a segunda cláusula de C9**, que a §18.7 mediu como impossível de
 * cumprir com fundo: `document.querySelectorAll('img').length === 0` na vitrine,
 * logo não existia onde pôr `loading="lazy"` nem `fetchpriority`. A nota de
 * projeto da própria régua já dizia — *"capa como `background-image` não aceita
 * `loading="lazy"` nem `fetchpriority`; quem quiser cumprir C9 provavelmente
 * troca por `<img>`"*.
 *
 * ## Por que o RECORTE não muda
 *
 * Porque `background-size: cover` + `background-position: center` e
 * `object-fit: cover` + `object-position: center` são o **mesmo algoritmo** na
 * especificação: a imagem é escalada pelo maior dos dois fatores e centrada, com
 * o excedente cortado pela caixa. A caixa continua a mesma — mesma
 * `aspect-ratio`, mesmo `overflow-hidden`, mesmo `border-radius` — e o `<img>`
 * ocupa exatamente ela (`absolute inset-0 h-full w-full`).
 *
 * A prova de que isto é fiel já estava no arquivo antes desta mudança: a prévia
 * animada (`vst-previa`) usa **estas mesmas classes** desde que nasceu, e ela se
 * sobrepõe ao cartaz parado sem que nada pule. Se o recorte de `object-cover`
 * diferisse do de `background: cover`, o pulo apareceria toda vez que alguém
 * passasse o mouse — e o teste `CapaAnimada.test.tsx` existe justamente sobre
 * essa sobreposição.
 *
 * ## Por que `alt=""`
 *
 * Porque a capa é decoração: o nome do curso está na legenda abaixo, em texto
 * (V5/R6), e o herói tem `h1` de HTML (V9). Um `alt` com o título faria o leitor
 * de tela ler o mesmo nome duas vezes seguidas. É a mesma decisão — e o mesmo
 * comentário — que a prévia animada já carrega.
 */
function CapaEmImagem({
  src,
  srcset,
  sizes,
  largura,
  altura,
  prioridade,
  className = '',
  corte,
  corteCelular,
}: {
  src: string;
  srcset?: string;
  sizes: string;
  largura?: number | null;
  altura?: number | null;
  /**
   * O herói é `high`; todo cartaz é preguiçoso.
   *
   * As duas necessidades são opostas e C9 pede as duas. O herói é a maior imagem
   * acima da dobra e o que a página inteira espera para parecer carregada — ele
   * concorre com o JS pela banda, e sem `fetchpriority` o navegador o trata como
   * uma imagem qualquer. O cartaz é o contrário: a prateleira rola na horizontal,
   * e a maioria dos cartazes de uma área com quarenta cursos nunca chega à tela.
   *
   * `loading="lazy"` no cartaz **não** adia o que já está visível: a regra do
   * navegador é sobre a viewport, não sobre a ordem. Os cinco primeiros carregam
   * na hora; o sexto em diante espera alguém arrastar.
   */
  prioridade?: 'heroi' | 'cartaz';
  className?: string;
  /** O corte do dono na tela e no celular (só o herói passa). Ver `estiloDoCorte`. */
  corte?: Corte | null;
  corteCelular?: Corte | null;
}) {
  const heroi = prioridade === 'heroi';
  const cortada = Boolean(corte || corteCelular);
  return (
    <img
      src={src}
      /* `|| undefined`: um `srcset=""` é legal e inofensivo, mas o atributo
         ausente é o que a ferramenta de auditoria consegue distinguir de
         "derivado ainda não gerado". */
      srcSet={srcset || undefined}
      sizes={sizes}
      /* Do ORIGINAL, e servem a um caso só: imagem bloqueada ou quebrada desenha
         no tamanho certo em vez de virar um ícone de 20px. A caixa já não pula,
         porque quem declara a proporção é ela, não a imagem. */
      width={largura ?? undefined}
      height={altura ?? undefined}
      alt=""
      aria-hidden
      loading={heroi ? 'eager' : 'lazy'}
      fetchPriority={heroi ? 'high' : undefined}
      /* `async` em tudo: decodificar no fio principal é o que faz a prateleira
         travar por um quadro quando cinco cartazes chegam juntos. */
      decoding="async"
      className={`absolute inset-0 h-full w-full object-cover ${cortada ? 'vst-corte' : 'object-center'} ${className}`}
      style={estiloDoCorte(corte, corteCelular)}
    />
  );
}

/**
 * O PRIMEIRO QUADRO de uma imagem que se mexe — para quem pediu menos movimento.
 *
 * ## Por que um `<canvas>`, e por que não há uma segunda imagem gravada
 *
 * O cartaz do herói (leva H) pode ser um GIF, e um GIF **não pausa**: parar é
 * tirar o `src`, que é o que `capa-animada.ts` faz no cartaz da prateleira —
 * mas lá existe um cartaz PARADO por baixo, e aqui não existe. O dono carregou
 * **um** arquivo, e pedir-lhe um segundo (um cartaz parado próprio) é uma
 * decisão de produto que não é do implementador.
 *
 * Então o quadro sai do próprio arquivo: a imagem é carregada fora da árvore e
 * desenhada UMA vez num canvas. O que aparece é o quadro que o decodificador
 * tinha no `load` — o primeiro. Depois disso nada mais é desenhado, e não há
 * laço nenhum a rodar.
 *
 * `drawImage` de uma origem cruzada é permitido; o que a origem cruzada proíbe
 * é **ler de volta** (`toDataURL`, `getImageData`), e não lemos. Isso importa:
 * as capas vêm assinadas do armazenamento, noutro domínio, e uma versão que
 * lesse os pixels quebraria com `SecurityError` só em produção.
 *
 * `object-fit` vale em `<canvas>` como vale em `<img>` (os dois são elementos
 * substituídos), então o enquadramento é o mesmo de `CapaEmImagem` — a mesma
 * classe, para as duas não divergirem.
 */
function CapaParada({
  src,
  largura,
  altura,
  className = '',
  corte,
  corteCelular,
}: {
  src: string;
  largura?: number | null;
  altura?: number | null;
  className?: string;
  corte?: Corte | null;
  corteCelular?: Corte | null;
}) {
  const tela = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = tela.current;
    if (!c) return;
    const img = new Image();
    img.onload = () => {
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext('2d')?.drawImage(img, 0, 0);
    };
    img.src = src;
    return () => {
      img.onload = null;
    };
  }, [src]);
  return (
    <canvas
      ref={tela}
      aria-hidden
      width={largura ?? undefined}
      height={altura ?? undefined}
      className={`absolute inset-0 h-full w-full object-cover ${corte || corteCelular ? 'vst-corte' : 'object-center'} ${className}`}
      style={estiloDoCorte(corte, corteCelular)}
    />
  );
}

/**
 * A imagem de um destaque, no encaixe que ela aguenta (`encaixeDoSlide`, core).
 *
 * `preencher` é o de sempre: cobre o herói, com o corte do dono ou com a deriva.
 *
 * `cartaz` é para a capa que esticada perderia a nitidez (vertical, ou estreita
 * — a do curso em destaque medida em 21/09 tinha 450 px de largura para um
 * herói de 1280–2000). A capa aparece INTEIRA e no tamanho em que é nítida, à
 * direita do texto; o fundo é ela mesma, desfocada e ampliada — o desfoque é
 * justamente o que esconde a falta de pixel, e mantém a cor do cartaz na tela
 * toda. No celular (herói 4/5, vertical) a capa vertical cabe no preencher, e
 * o pôster ao lado só existe a partir de `sm`.
 */
/*
 * ── EXPORTADA EM 08/10/2026, E É ELA QUE RESOLVE O CASO DIFÍCIL ────────────
 *
 * O dono pediu o cartaz da trilha como *«capa de section hero de borda a
 * borda»*. A página da trilha JÁ TEVE uma faixa larga, e ela foi removida a
 * 13/09 com a medição ao lado: a arte do tutor é um cartaz 3/4, e um
 * `aspect-[21/9]` com `object-cover` guardava um quarto da altura e deitava
 * fora o resto.
 *
 * Esta função é a razão de a ordem do dono e aquela medição não se
 * contradizerem: com uma imagem vertical ela **não estica** — põe a capa
 * desfocada no fundo e o cartaz INTEIRO por cima, que é o que a vitrine já faz
 * desde 21/09, quando o dono disse *«as imagens da capa, quando são verticais,
 * perdem a resolução quando estão em destaque no hero»*.
 *
 * Reusada e não copiada: uma segunda decisão de encaixe é como as duas telas
 * passam a recortar o mesmo ficheiro de maneiras diferentes.
 */
export function CapaDoSlide({ slide }: { slide: SlideDoHeroi }) {
  if (!slide.imagem) return null;
  const cartaz = encaixeDoSlide(slide) === 'cartaz';
  if (!cartaz) {
    return (
      <CapaEmImagem
        src={slide.imagem}
        srcset={slide.imagemSrcset}
        sizes={SIZES_DO_HEROI}
        largura={slide.imagemLargura}
        altura={slide.imagemAltura}
        prioridade="heroi"
        className={slide.enquadramento || slide.enquadramentoCelular ? '' : 'vst-deriva'}
        corte={slide.enquadramento}
        corteCelular={slide.enquadramentoCelular}
      />
    );
  }
  return (
    <>
      {/* O fundo: no celular é o preencher de sempre; da `sm` em diante, desfocado. */}
      <div
        aria-hidden
        className="absolute inset-0 sm:scale-110 sm:blur-2xl sm:brightness-75"
        data-vst-heroi-fundo-desfocado=""
      >
        <CapaEmImagem
          src={slide.imagem}
          srcset={slide.imagemSrcset}
          sizes={SIZES_DO_HEROI}
          largura={slide.imagemLargura}
          altura={slide.imagemAltura}
          prioridade="heroi"
          className="vst-deriva"
        />
      </div>
    </>
  );
}

/**
 * Onde o pôster se pendura dentro do herói, e por que são DUAS geometrias e não
 * uma com um `left` trocado.
 *
 * ── `direita` ───────────────────────────────────────────────────────────────
 * A da vitrine, desde 21/09/2026. O pôster encosta na margem e o texto ocupa a
 * esquerda: os dois PARTILHAM a faixa de altura, e é por isso que o título leva
 * um teto de largura — sem ele passa por baixo da arte.
 *
 * ── `centro` ────────────────────────────────────────────────────────────────
 * Ordem do dono, 08/10/2026, sobre a página da trilha: *«coloque essa capa no
 * centro»*.
 *
 * E centrar não é trocar `right-12` por `left-1/2`: **a coluna de texto desta
 * casa também é centrada**, logo um pôster centrado cai exactamente em cima
 * dela. A conta, com a arte 848×1264 do dono: o pôster centrado começa a
 * `0,43·L` da esquerda, e a coluna (`max-w-4xl` centrada) começa a `0,5·L −
 * 416`; o que sobra para o título é `416 − 0,07·L` — **281 px a 1456, e menos
 * quanto maior a janela**. Um título a 36 px em 281 px não é uma linha, é uma
 * pilha.
 *
 * Então o centrado NÃO partilha a faixa com o texto. E a maneira de o garantir
 * mudou no mesmo dia, por uma segunda ordem do dono — *«encurte essa altura»*:
 *
 * ── A PRIMEIRA VOLTA PÔS O CENTRADO EM `absolute`, E PAGOU EM ALTURA ────────
 *
 * Com `top-[6%] h-[58%]`, a altura do pôster é uma FRACÇÃO da banda, e a folga
 * até ao texto é o que sobra dos 36% restantes. Para o pôster ter 334 px a
 * banda tinha de ter 576 — e quando a trilha não tem subtítulo, o texto encolhe
 * e a folga **cresce**: o vazio fotografado pelo dono. A banda mandava no
 * pôster, e ninguém mandava no vazio.
 *
 * ── O CENTRADO É AGORA UM IRMÃO EM FLUXO ───────────────────────────────────
 *
 * `relative mx-auto`, com altura PRÓPRIA. Deixa de haver fracção: o pôster mede
 * o que mede, o texto vem a seguir, e a banda (que no `cartaz` passa a
 * `aspect-ratio: auto`) é a soma. **Sobreposição e vazio tornam-se os dois
 * impossíveis por construção** — são irmãos numa coluna, não duas camadas a
 * disputar a mesma caixa.
 *
 * `clamp(190px, 17vw, 310px)`: segue a janela como a fracção seguia, mas com
 * chão e teto, e sem arrastar a altura da banda atrás dela.
 *
 * O `-z-10` fica só no `direita`. Em fluxo, um `-z-10` punha o pôster ao nível
 * do véu e do fundo — e a arte que se quis mostrar inteira ficava por baixo da
 * rampa que existe para servir o texto.
 */
const ANCORA_DO_CARTAZ = {
  direita: 'absolute -z-10 right-6 top-[8%] h-[84%] sm:right-12',
  centro: 'relative mx-auto mb-5 mt-8 h-[clamp(190px,17vw,310px)]',
} as const;

export type AncoraDoCartaz = keyof typeof ANCORA_DO_CARTAZ;

/**
 * O pôster do encaixe `cartaz` — fora da camada da imagem, e DEPOIS do véu: o
 * véu serve o texto, e escurecer a capa que se quis mostrar inteira seria
 * desfazer o motivo do encaixe.
 *
 * A posição vem INTEIRA de `ANCORA_DO_CARTAZ` — incluindo o `absolute` e o
 * `-z-10` — e **não** do `className` de quem chama: `position`, `top`, `height`
 * e `right` são a mesma propriedade em duas classes do Tailwind, e qual delas
 * vence é a ordem na FOLHA, não a ordem na string. Uma «sobreposição» por
 * `className` funcionaria ou não conforme o dia em que o CSS foi gerado.
 */
export function CartazDoSlide({
  slide,
  className = '',
  ancora = 'direita',
}: {
  slide: SlideDoHeroi;
  className?: string;
  ancora?: AncoraDoCartaz;
}) {
  if (!slide.imagem || encaixeDoSlide(slide) !== 'cartaz') return null;
  return (
    <>
      {/* A altura manda, a largura sai da proporção da própria imagem. */}
      <img
        src={slide.imagem}
        srcSet={slide.imagemSrcset || undefined}
        sizes="(min-width: 640px) 40vw, 0px"
        width={slide.imagemLargura ?? undefined}
        height={slide.imagemAltura ?? undefined}
        alt=""
        aria-hidden
        decoding="async"
        data-vst-heroi-cartaz=""
        data-vst-ancora-do-cartaz={ancora}
        className={`hidden w-auto max-w-[42%] object-contain sm:block ${ANCORA_DO_CARTAZ[ancora]} ${className}`}
        style={{
          borderRadius: 'var(--raio)',
          boxShadow:
            '0 24px 60px -12px rgb(0 0 0 / 0.6), 0 0 0 1px color-mix(in srgb, var(--texto) 10%, transparent)',
        }}
      />
    </>
  );
}

/**
 * Os controles do carrossel só existem — e só são baixados — com mais de um
 * slide. Ver `Carrossel.tsx` e a cerca `o-carrossel-e-um-pedaco-a-parte`.
 */
const Carrossel = telaComDicionario(() =>
  import('./Carrossel.js').then((m) => ({ default: m.Carrossel })),
);

/**
 * Quanto tempo a camada que SAI fica montada, em milissegundos.
 *
 * A animação mais longa dela é `vst-troca-sai`, 780ms em `index.css`. Os 40ms a
 * mais são folga: arrancar a camada no milissegundo exato em que a animação
 * acaba é apostar que o relógio do `setTimeout` e o do compositor concordam, e
 * eles não concordam — o que sobra de um desacordo é um piscar no fim da troca.
 *
 * Mexer aqui sem mexer lá (ou o contrário) é o defeito clássico desta dupla: se
 * o número da folha crescer acima de 820, a camada é removida a meio do
 * desbotar. Os dois números estão nomeados nos dois lugares de propósito.
 */
const MS_DA_PASSAGEM = 820;

/**
 * A coluna de texto de UM slide — sem a caixa, só as linhas.
 *
 * Existe como componente porque numa troca ela é desenhada **duas vezes ao mesmo
 * tempo**: a que sai e a que entra. Duas cópias do mesmo JSX é exatamente como
 * as duas param de concordar seis meses depois, quando alguém acrescenta uma
 * linha só na de cima.
 *
 * `cascata` é a única diferença entre os dois momentos, e é uma decisão de
 * produto, não de estilo: no CARREGAMENTO cada linha entra por `vst-entra` com o
 * seu atraso, porque a ordem de leitura é a informação para quem acabou de
 * chegar; numa TROCA a coluna inteira entra de uma vez (o pai leva
 * `vst-troca-texto-entra`), porque quem já está lendo não deve esperar linha por
 * linha por palavras que já sabe onde ficam. O argumento longo está em
 * `index.css`, no bloco «A PASSAGEM ENTRE DESTAQUES».
 */
function TextoDoSlide({
  slide,
  areaSlug,
  cascata,
  compacto = false,
  tranca = null,
  soVoceVe = null,
}: {
  slide: SlideDoHeroi;
  areaSlug: string;
  cascata: boolean;
  /** Os degraus baixos (`faixa`, `curta`): o texto encolhe para caber na caixa rasa. */
  compacto?: boolean;
  /** O curso deste slide espera outro: o botão sai e o motivo toma o lugar. */
  tranca?: TrancaDeOrdem | null;
  /**
   * O curso deste slide **não existe para o aluno** (prévia da bancada): o botão
   * sai, e o carimbo com a frase da estreia toma o lugar. Ver `SoVoceVeNoHeroi`.
   */
  soVoceVe?: { lancamento: Lancamento | null } | null;
}) {
  const t = useT();
  const c = cascata ? 'vst-entra ' : '';
  /* O atraso é do componente, não da folha: a sequência é uma decisão daqui. */
  const d = (ms: number) => (cascata ? { animationDelay: `${ms}ms` } : undefined);
  return (
    <>
      {/* «Em destaque» é o vocabulário do catálogo: o slide de item promete
          um curso. O cartaz não promete curso nenhum, e não usa a palavra. */}
      {slide.para !== null && (
        <p
          className={`${c}inline-flex w-fit items-center px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.24em]`}
          style={{
            color: 'var(--destaque)',
            background: 'var(--fundo)',
            borderRadius: 'calc(var(--raio) * 0.5)',
            ...d(40),
          }}
        >
          {t('vitrine.emDestaque')}
        </p>
      )}
      {slide.titulo && (
        <h1
          className={`${c}mt-2 font-semibold leading-[1.05] tracking-tight ${compacto ? 'text-2xl sm:text-4xl' : 'text-4xl sm:text-6xl'}`}
          style={d(120)}
        >
          {slide.titulo}
        </h1>
      )}
      {slide.texto && (
        <p
          className={`${c}max-w-xl leading-relaxed ${compacto ? 'mt-2 line-clamp-2 text-sm sm:text-base' : 'mt-4 text-base sm:text-lg'}`}
          style={{ color: 'var(--texto-suave)', ...d(200) }}
        >
          {slide.texto}
        </p>
      )}
      {/*
       * O botão SAI quando o curso do cartaz espera outro (24/09/2026).
       *
       * «Começar» num curso que o servidor recusa é a maior promessa da página
       * a levar ao 403 — e um botão inerte com a cara do botão de sempre seria
       * pior, porque continua a prometer. O que fica no lugar é o motivo, com o
       * nome do que falta a levar para lá: o cartaz deixa de oferecer a porta e
       * passa a apontar a chave.
       */}
      {/*
       * ── E SAI TAMBÉM QUANDO O ALUNO NÃO VÊ O CURSO (04/11/2026) ───────────
       *
       * Antes da tranca, e a ordem não é arbitrária: «o aluno não vê isto» é o
       * fato mais de fora que existe sobre este cartaz, e um pré-requisito
       * desenhado por cima dele responderia uma pergunta que ninguém fez — a
       * ordem de estudo de um curso que ainda não é de ninguém. É a mesma
       * precedência que `CapaDeCurso` aplica na faixa de baixo.
       */}
      {slide.para !== null && soVoceVe && (
        <div className={`${c}${compacto ? 'mt-4' : 'mt-6'} max-w-xl`} style={d(280)}>
          <SoVoceVeNoHeroi lancamento={soVoceVe.lancamento} />
        </div>
      )}
      {slide.para !== null && !soVoceVe && tranca && (
        <div className={`${c}${compacto ? 'mt-4' : 'mt-6'} max-w-xl`} style={d(280)}>
          <MotivoDaTranca tranca={tranca} areaSlug={areaSlug} />
        </div>
      )}
      {slide.para !== null && !soVoceVe && !tranca && (
        <div
          className={`${c}${compacto ? 'mt-4' : 'mt-7'} flex flex-wrap items-center gap-4`}
          style={d(280)}
        >
          <Link
            to={caminhoDaArea(areaSlug, slide.para)}
            data-vst-heroi-para={slide.para}
            className={`inline-flex items-center gap-2 font-medium transition-transform hover:scale-[1.03] ${compacto ? 'px-5 py-2 text-sm' : 'px-7 py-3'}`}
            style={{
              background: 'var(--destaque)',
              color: 'var(--sobre-destaque, #08130c)',
              borderRadius: 'var(--raio)',
            }}
          >
            {slide.botao}
            <span aria-hidden>→</span>
          </Link>
          {(slide.linha || slide.progresso !== null) && (
            <span className="text-sm tabular-nums" style={{ color: 'var(--texto-suave)' }}>
              {slide.linha}
              {slide.progresso !== null && ` · ${slide.progresso}% assistido`}
            </span>
          )}
        </div>
      )}
    </>
  );
}

/**
 * O slide de um corpo ANTIGO (sem `slides`): o herói de sempre, derivado de
 * `curso/imagem/titulo/texto`. É a mesma derivação de `slidesGravados`, no
 * core, feita do lado de cá com o curso já resolvido — para a prévia de um
 * painel mais velho e para os testes que montam a vitrine à mão.
 */
/**
 * ── A LEGENDA DO CARTÃO DE CURSO: «48 aulas · 10 h 42 min» (0439) ───────────
 *
 * Ordem do dono de 28/09/2026: a carga horária ao lado da contagem de aulas.
 *
 * ## Uma função, três sítios
 *
 * A mesma string é desenhada no herói (`slideLegado`), no cartão com data de
 * estreia e no cartão normal — e ela já era a mesma nos três antes desta obra,
 * escrita à mão três vezes. Acrescentar a carga em três sítios seria a maneira
 * de o quarto ficar para trás no dia em que alguém mudar o separador.
 *
 * ## Aulas primeiro, carga depois
 *
 * A contagem é a unidade de navegação do produto, e é ela que o aluno usa para
 * decidir por onde entrar. E há um efeito prático que fecha a decisão: numa área
 * que não liga o recurso, esta função devolve **byte a byte** a string que já
 * estava no ar.
 *
 * ## `duracao` do core, e não `duracaoLegivel` daqui
 *
 * Porque este número é lido em três sítios pelo aluno (cartão, total, sala) e
 * num quarto pelo TUTOR, na frase do painel que lhe diz o que o aluno vê. Dois
 * formatos fariam alguém comparar `10h 42` com `10 h 42 min` e ter de adivinhar
 * se são o mesmo. A casa já tem dois formatos de duração a mais do que devia;
 * esta obra escolheu um deles em vez de estrear o terceiro.
 */
/**
 * O rótulo do cartão para quem ouve a tela.
 *
 * Irmão de `legendaDoCurso`, e separado por uma razão só: aqui a grandeza vai
 * **por extenso** (`10 horas e 42 minutos`), porque `10 h 42 min` é lido em voz
 * alta como «dez h quarenta e dois min». O bloqueado mantém a frase que já
 * tinha — ele não anuncia tamanho nem aqui.
 */
export function rotuloDoCartao(curso: {
  title: string;
  aulas: number;
  cargaSegundos?: number | null;
  bloqueado?: boolean;
}): string {
  if (curso.bloqueado) return `${curso.title} — você ainda não tem`;
  const aulas = `${curso.aulas} ${curso.aulas === 1 ? 'aula' : 'aulas'}`;
  const carga = cargaPorExtenso(curso.cargaSegundos);
  return carga === '' ? `${curso.title} — ${aulas}` : `${curso.title} — ${aulas}, ${carga}`;
}

export function legendaDoCurso(curso: { aulas: number; cargaSegundos?: number | null }): string {
  const aulas = `${curso.aulas} ${curso.aulas === 1 ? 'aula' : 'aulas'}`;
  /* Ramo, e nunca interpolação: `duracao` devolve `null` para ausência, e um
     template produziria «48 aulas · null». */
  const carga = duracao(curso.cargaSegundos ?? null);
  return carga === null ? aulas : `${aulas} · ${carga}`;
}

export function slideLegado(
  h: NonNullable<DadosDaVitrine['heroi']>,
  curso: CursoDaVitrine | undefined,
): SlideDoHeroi[] {
  if (!curso) return [];
  const comecado = Boolean(curso.progresso && curso.progresso.percent > 0);
  return [
    {
      chave: `curso:${curso.id}`,
      tipo: 'curso',
      id: curso.id,
      cursoId: curso.id,
      para: `/c/${curso.id}`,
      titulo: h.titulo || curso.title,
      texto: h.texto || curso.subtitle,
      linha: legendaDoCurso(curso),
      botao: comecado ? 'Continuar' : 'Começar',
      imagem: h.imagem ?? curso.capa,
      imagemSrcset: h.imagem ? h.imagemSrcset : curso.capaSrcset,
      imagemLargura: h.imagem ? h.imagemLargura : curso.capaLargura,
      imagemAltura: h.imagem ? h.imagemAltura : curso.capaAltura,
      escurecer: h.escurecer,
      progresso: comecado ? curso.progresso!.percent : null,
    },
  ];
}

/**
 * O bloco grande no topo — agora por SLIDE (leva A3).
 *
 * Um slide só é o herói de sempre, byte a byte; com mais de um, o carrossel
 * entra por um pedaço à parte. A altura é uma PROPORÇÃO declarada
 * (`aspectoDoHeroi`, V8) escrita em duas variáveis que `index.css` lê —
 * `.vst-heroi` — em vez de uma classe cravada.
 */
function Heroi({
  dados,
  slides,
  /*
   * O mapa de cursos, e não um campo próprio no slide (24/09/2026).
   *
   * O slide já traz `cursoId` e `cursos[]` já traz `tranca`: uma fonte só, e a
   * tela cruza. Um segundo campo com a mesma verdade é o começo do dia em que
   * o cartaz diz uma coisa e o cartão do mesmo curso diz outra.
   */
  cursos,
}: {
  dados: Omit<DadosDaVitrine, 'cursos'>;
  slides: SlideDoHeroi[];
  cursos: Map<string, CursoDaVitrine>;
}) {
  const h = dados.heroi!;
  /* O cartaz é a maior promessa da página: se o curso dele espera outro, é aqui
     que o aviso mais falta — e era aqui que o botão levava ao 403. */
  const trancaDoSlide = (s: SlideDoHeroi) =>
    s.cursoId ? lerTranca(cursos.get(s.cursoId)?.tranca) : null;
  /*
   * ── O DESTAQUE QUE SÓ O TUTOR VÊ (04/11/2026) ─────────────────────────────
   *
   * Pelo MAPA, como a tranca da linha de cima — e não por um campo no slide. A
   * razão está no jsdoc de `cursos`, e vale igual aqui: um segundo campo com a
   * mesma verdade é o começo do dia em que o cartaz diz uma coisa e o cartão do
   * mesmo curso diz outra. É o mesmo `curso.soVoceVe` que `CapaDeCurso` lê.
   *
   * Devolve o objeto (e não um booleano) porque quem desenha precisa dos DOIS
   * fatos: que o aluno não vê, e quando é que ele vai ver. `lerLancamento` é a
   * mesma função do cartão, de modo que a frase da estreia é por construção a
   * mesma nas duas peças.
   */
  const soVoceVeDoSlide = (s: SlideDoHeroi) => {
    const c = s.cursoId ? cursos.get(s.cursoId) : undefined;
    return c?.soVoceVe === true ? { lancamento: lerLancamento(c.lancamento) } : null;
  };
  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);
  const atual = slides[Math.min(indice, slides.length - 1)]!;
  const varios = slides.length > 1;
  const aspecto = aspectoDoHeroi(h.altura);
  const compacto = heroiCompacto(h.altura);
  const ir = useCallback((i: number) => setIndice(i), []);

  /*
   * A bancada pede um destaque (o dono abriu-o na lista): o herói vai até ele e
   * SEGURA — um relógio a trocar o slide enquanto se ajusta o corte dele seria
   * editar às cegas. `null` devolve o relógio. O evento só existe dentro da
   * prévia (`modo-estudio.ts`); o aluno nunca o recebe.
   */
  const [segurado, setSegurado] = useState(false);
  useEffect(() => {
    const aoVer = (e: Event | { detail: string | null | undefined }) => {
      const chave = (e as { detail?: string | null }).detail;
      if (!chave) return setSegurado(false);
      const i = slides.findIndex((s) => s.chave === chave || s.chave.endsWith(`:${chave}`));
      if (i === -1) return;
      setIndice(i);
      setSegurado(true);
    };
    const guardado = (window as { __vstVerSlide?: string | null }).__vstVerSlide;
    if (guardado) aoVer({ detail: guardado });
    window.addEventListener('vst:ver-slide', aoVer);
    return () => window.removeEventListener('vst:ver-slide', aoVer);
  }, [slides]);

  /*
   * ── A PASSAGEM: as duas camadas que coexistem durante a troca ─────────────
   *
   * O defeito era estrutural. A camada da imagem leva `key={chave}`: o React
   * desmontava a velha e montava a nova, e a nova surgia por opacidade SOBRE O
   * FUNDO. Havia um quadro sem imagem nenhuma, e é isso que se lia como «bruto»
   * — nenhuma curva conserta um vazio. O texto tinha o mesmo mal ao contrário: o
   * velho sumia no mesmo instante, sem saída.
   *
   * Então o herói passa a lembrar-se do slide que estava a mostrar. Enquanto
   * `saindo` existe, o cartaz tem DUAS camadas de imagem e DUAS colunas de
   * texto; `MS_DA_PASSAGEM` depois, a velha é removida.
   *
   * ## Três coisas que este estado carrega, e por que cada uma
   *
   * - **`saindo`** é o objeto do slide anterior, e não uma busca por chave em
   *   `slides`: na bancada o dono edita a lista enquanto a prévia corre, e uma
   *   busca devolveria `undefined` (ou, pior, o slide errado) exatamente no
   *   instante da troca.
   * - **`primeira`** distingue o CARREGAMENTO da TROCA. É o que escolhe entre
   *   `vst-surge`/cascata e `vst-troca-*` — e, por ser estado e não um cálculo
   *   por render, a classe de cada camada é decidida no momento em que ela nasce
   *   e nunca muda debaixo dela. Trocar a classe de um elemento vivo reinicia a
   *   animação: seria um segundo desbotar, do nada, no fim de cada troca.
   * - **A gravação é aqui, no render**, e não num `useEffect`. Um efeito só corre
   *   depois de o navegador ter pintado, e nesse quadro a camada velha já não
   *   existiria — o buraco de volta, agora com mais código.
   *
   * ## Quem pediu menos movimento não entra nisto
   *
   * `comMovimento` é falso com `prefers-reduced-motion: reduce` ou com economia
   * de dados, e aí `saindo` nunca é gravado: a troca é um corte limpo, com uma
   * camada só. É o cinto. O suspensório é `index.css`, onde as classes
   * `vst-troca-*` só existem dentro de `no-preference` — e nenhuma delas põe
   * `opacity: 0` fora de um `@keyframes`, que é como um elemento fica invisível
   * para sempre quando alguém desliga a animação por baixo dele.
   */
  const comMovimento = podeAnimar(lerAmbienteDoMovimento());
  const [passagem, setPassagem] = useState<{
    atual: SlideDoHeroi;
    saindo: SlideDoHeroi | null;
    primeira: boolean;
  }>({ atual, saindo: null, primeira: true });
  if (passagem.atual.chave !== atual.chave) {
    setPassagem({ atual, saindo: comMovimento ? passagem.atual : null, primeira: false });
  }
  const saindo = passagem.saindo;
  useEffect(() => {
    if (!saindo) return;
    const t = setTimeout(
      () => setPassagem((p) => (p.saindo ? { ...p, saindo: null } : p)),
      MS_DA_PASSAGEM,
    );
    return () => clearTimeout(t);
  }, [saindo]);

  /*
   * ── «SEM LINK» É UM FATO, E O FATO É `para === null` (leva H) ────────────
   *
   * O cartaz do dono não leva a lugar nenhum. Isso não se implementa deixando
   * de escrever um `href`: um bloco que PARECE clicável e não é fica pior que
   * um que não parece. Então o `null` do servidor apaga as três coisas que
   * prometem clique — o `<Link>`, o botão e a legenda de progresso — e a região
   * DECLARA o que é, para quem mede e para quem só olha.
   */
  const clicavel = atual.para !== null;
  const temTexto = Boolean(atual.titulo || atual.texto);

  /*
   * O véu serve o TEXTO, e num cartaz sem texto não há o que servir.
   *
   * `veuDoHeroi` existe porque texto claro sobre imagem clara some. Escurecer
   * 55% da arte que o dono acabou de carregar, para proteger um texto que ele
   * escolheu não escrever, é estragar a única coisa que o cartaz tem. Escreveu
   * título ou chamada, o véu volta — e o controle dele continua onde estava.
   */
  const comVeu = temTexto || clicavel;

  /*
   * O movimento: quem pediu menos movimento (ou economia de dados) vê o
   * primeiro quadro. `podeAnimar` já está nesta tela por causa do cartaz da
   * prateleira — custa zero byte novo. Ver `CapaParada` para o que se perde.
   */
  const parar = atual.animada === true && !podeAnimar(lerAmbienteDoMovimento());

  return (
    <section
      data-vst-regiao="heroi"
      data-vst-slides={slides.length}
      data-vst-heroi-clicavel={clicavel ? 'sim' : 'nao'}
      aria-roledescription={varios ? 'carrossel' : undefined}
      onMouseEnter={varios ? () => setPausado(true) : undefined}
      onMouseLeave={varios ? () => setPausado(false) : undefined}
      onFocus={varios ? () => setPausado(true) : undefined}
      onBlur={varios ? () => setPausado(false) : undefined}
      data-vst-heroi-altura={h.altura ?? 'media'}
      className={`vst-heroi relative isolate flex max-h-[82vh] w-full items-end overflow-hidden ${compacto ? 'p-5 sm:p-8' : 'p-6 sm:p-12'}`}
      style={
        {
          background: 'var(--superficie)',
          /* O piso por degrau (era 420 cravado): com ele as alturas baixas seriam só nomes. */
          minHeight: `${minimoDoHeroi(h.altura)}px`,
          '--heroi-celular': aspecto.celular,
          '--heroi-tela': aspecto.tela,
        } as React.CSSProperties
      }
    >
      {/* A camada que deriva. Continua sendo uma camada própria — e não o
          `section` — porque quem se move é a imagem, e não a caixa: mover a
          caixa levaria o texto junto. `key` pelo slide: a imagem nova entra
          com a mesma deriva da primeira.

          O ENVOLTÓRIO é novo, e não é enfeite: `vst-deriva` já é dona do
          `transform` do `<img>`, e uma segunda animação de `transform` no mesmo
          elemento sobrescreve a primeira — a deriva morreria em silêncio. O
          assentar mora uma camada acima, e as duas compõem por aninhamento.

          `vst-surge` no carregamento, `vst-troca-entra` a partir da primeira
          troca. A escolha é feita quando o envoltório nasce (a `key` muda a cada
          slide) e nunca muda debaixo dele. */}
      {atual.imagem &&
        (parar ? (
          <CapaParada
            key={atual.chave}
            src={atual.imagem}
            largura={atual.imagemLargura}
            altura={atual.imagemAltura}
            className="-z-10"
            corte={atual.enquadramento}
            corteCelular={atual.enquadramentoCelular}
          />
        ) : (
          <div
            key={`${atual.chave}:capa`}
            data-vst-heroi-capa=""
            className={`absolute inset-0 -z-10 ${passagem.primeira ? 'vst-surge' : 'vst-troca-entra'}`}
          >
            <CapaDoSlide slide={atual} />
          </div>
        ))}
      {/*
        A camada que SAI — a metade que faltava para haver dissolve em vez de corte.

        ## A `key` é a mesma que esta camada tinha quando estava a entrar

        `${saindo.chave}:capa` é, letra por letra, a `key` que o envoltório acima
        usava no render anterior. Isso não é economia de nomes: é o que faz o
        React **reaproveitar o nó do DOM** em vez de destruí-lo e criar outro. O
        `<img>` lá dentro nunca é recriado, então a imagem que estava pintada
        continua pintada no primeiro quadro da saída. Com uma `key` nova, o
        navegador teria de repintar (e possivelmente redecodificar) a imagem
        velha no exato quadro em que ela devia estar a segurar a tela — e o
        buraco de um quadro voltaria, agora do outro lado.

        O que muda no nó reaproveitado é só a classe e o `z`: ele desce para
        `-z-20`, abaixo da camada que entra (`-z-10`) e do véu (`-z-10`), e ganha
        `vst-troca-sai`. A ordem no DOM deixa de decidir a pilha — quem decide é
        o `z-index`, e é por isso que a camada que sai pode vir DEPOIS aqui: a
        primeira `[data-vst-heroi-para]` do cartaz continua a ser a do slide
        atual, que é o que as cercas e o leitor de tela leem.

        `parar` (GIF sem movimento permitido) nunca chega aqui: sem movimento não
        há `saindo`.
      */}
      {saindo?.imagem && !parar && (
        <div
          key={`${saindo.chave}:capa`}
          data-vst-heroi-capa-saindo=""
          aria-hidden
          className="vst-troca-sai absolute inset-0 -z-20"
        >
          <CapaDoSlide slide={saindo} />
        </div>
      )}
      {/* O véu: paradas em pixel, ver `veuDoHeroi`. A marca existe para a camada
          ser CONTÁVEL — sem ela, «o cartaz sem texto não leva véu» só se mede
          farejando um `linear-gradient` num estilo calculado, e uma cerca assim
          fica verde no dia em que a parada mudar. */}
      {comVeu && (
        <div
          data-vst-heroi-veu=""
          className="absolute inset-0 -z-10"
          style={{ background: veuDoHeroi(atual.escurecer) }}
        />
      )}
      {!parar && (
        <CartazDoSlide
          key={`${atual.chave}:cartaz`}
          slide={atual}
          className={passagem.primeira ? 'vst-surge' : 'vst-troca-texto-entra'}
        />
      )}
      {/*
        A coluna de texto só existe quando há texto. Um cartaz sem título e sem
        chamada é a arte e nada mais — nem `<h1>` vazio (que o leitor de tela
        anunciaria como um cabeçalho sem nome), nem caixa vazia por cima da
        imagem. A imagem continua decorativa (`alt=""`), porque quem a descreve,
        quando há o que descrever, é o título.
      */}
      {(temTexto || clicavel) && (
        /*
          A CAIXA das colunas de texto — novo, e é ela que permite a coexistência.

          `isolate` cria um contexto de empilhamento próprio. Sem ele, a coluna
          que sai (com `-z-10`) cairia atrás do VÉU do cartaz, que também é
          `-z-10` mas vive na pilha do `section`; com ele, o `-z-10` é relativo a
          esta caixa e a coluna que sai fica logo abaixo da que entra, e acima da
          arte. É uma linha, e é a diferença entre uma saída visível e uma saída
          por baixo da imagem.

          A caixa NÃO leva o `pb-14`: quem o leva são as duas colunas, para que
          as duas fiquem ancoradas na mesma linha de base quando a de saída for
          posicionada por `bottom-0`. Pô-lo aqui deslocaria a de saída em 56px
          para baixo no primeiro quadro da troca, que é o «salto» outra vez.
        */
        <div
          className={`relative isolate max-w-2xl ${atual.imagem && encaixeDoSlide(atual) === 'cartaz' ? 'sm:max-w-[52%]' : ''}`}
        >
          <div
            /* `:texto` porque a camada da imagem é irmã e usa a mesma chave: dois
               filhos com a MESMA `key` no mesmo pai é aviso do React desde sempre. */
            key={`${atual.chave}:texto`}
            className={`relative ${varios ? 'pb-14 sm:pb-16' : ''} ${passagem.primeira ? '' : 'vst-troca-texto-entra'}`}
            aria-live={varios ? 'polite' : undefined}
          >
            <TextoDoSlide
              slide={atual}
              areaSlug={dados.area.slug}
              cascata={passagem.primeira}
              compacto={compacto}
              tranca={trancaDoSlide(atual)}
              soVoceVe={soVoceVeDoSlide(atual)}
            />
          </div>
          {/*
            A coluna que SAI.

            Mesma `key` que ela tinha quando era a coluna de entrada, pela mesma
            razão da imagem: o React reaproveita o nó, e o que estava desenhado
            continua desenhado no primeiro quadro da saída.

            ## As duas armadilhas de acessibilidade que a coexistência cria

            1. **`aria-live` sai daqui.** A caixa de texto é uma região viva
               quando há vários slides; com a coluna velha e a nova montadas ao
               mesmo tempo, um leitor de tela poderia anunciar as duas — a pessoa
               ouviria o destaque que ela acabou de deixar. Só a coluna que ENTRA
               é viva. A que sai é ex-conteúdo: `aria-hidden` tira-a da árvore de
               acessibilidade.
            2. **`inert` tira-a da ordem de foco.** `aria-hidden` sozinho num
               bloco que contém um `<Link>` é um defeito conhecido — o leitor não
               a anuncia, mas o Tab ainda pousa nela, e a pessoa fica com o foco
               num link que ninguém consegue descrever. `inert` fecha o outro
               lado, e `pointer-events-none` fecha o do rato: durante 180ms há um
               botão a desbotar por cima do novo, e clicar nele levaria ao
               destaque errado.

            `absolute inset-x-0 bottom-0`: fora do fluxo, para a caixa não virar
            duas linhas empilhadas — que seria um salto de layout, o defeito que
            esta leva veio corrigir.
          */}
          {saindo && (
            <div
              key={`${saindo.chave}:texto`}
              data-vst-heroi-texto-saindo=""
              aria-hidden
              inert
              className={`vst-troca-texto-sai pointer-events-none absolute inset-x-0 bottom-0 -z-10 ${varios ? 'pb-14 sm:pb-16' : ''}`}
            >
              <TextoDoSlide
                slide={saindo}
                areaSlug={dados.area.slug}
                cascata={false}
                compacto={compacto}
                tranca={trancaDoSlide(saindo)}
                soVoceVe={soVoceVeDoSlide(saindo)}
              />
            </div>
          )}
        </div>
      )}
      {varios && (
        <Suspense fallback={null}>
          <Carrossel
            total={slides.length}
            indice={indice}
            aoIr={ir}
            avanco={h.avanco ?? 'automatico'}
            intervaloSegundos={h.intervaloSegundos ?? 5}
            pausado={pausado || segurado}
          />
        </Suspense>
      )}
    </section>
  );
}

/**
 * O trilho só é um rolador quando há para onde rolar.
 *
 * ## Por que isto precisa ser medido, e não contado
 *
 * "Um cartão só" não é a pergunta: três cartões rolam a 390px e não rolam a
 * 1280. Quem sabe se cabe é o layout depois de pronto, e é por isso que a
 * resposta vem do `ResizeObserver` e não de `lista.length`.
 *
 * ## Por que o `overflow-x` NÃO é desligado junto
 *
 * Porque ele é a rede que impede a prateleira de empurrar a página inteira para
 * a direita — o defeito que o herói acabou de custar. Desligá-lo com base numa
 * medição significa que qualquer erro de um pixel vira barra de rolagem
 * horizontal no documento (C10). E o `overflow-x` sozinho não desenha controle
 * nenhum: a barra está escondida, e um trilho que não transborda não se move.
 *
 * O que mentia era o **snap**. `scroll-snap-type: x mandatory` declara que há
 * posições para onde deslizar; num trilho de 1265px com um cartão de 205px não
 * há nenhuma, e é o mesmo controle que gira entre um item só — o argumento que
 * o herói já usa para não ter carrossel. Desligar o snap não move um pixel do
 * layout, então também não há laço de medição.
 */
function useCabeNoTrilho(quantos: number): [React.RefObject<HTMLDivElement | null>, boolean] {
  const trilho = useRef<HTMLDivElement>(null);
  const [cabe, setCabe] = useState(true);

  /*
   * `quantos` na lista de dependências, e não só a montagem.
   *
   * O `ResizeObserver` pega a janela mudando de tamanho; não pega a prateleira
   * mudando de conteúdo. E ela muda: no Estúdio, a prévia é redesenhada a cada
   * curso que entra numa prateleira, e uma prateleira que ganhou o sexto cartão
   * ficaria sem snap para sempre — o defeito trocado de lado.
   */
  useEffect(() => {
    const el = trilho.current;
    if (!el) return;

    /* 1px de folga: `scrollWidth` e `clientWidth` são inteiros arredondados de
       larguras fracionárias, e um `clamp(…, 16vw, …)` produz fração o tempo
       todo. Sem a folga, o snap piscaria ao arrastar a janela. */
    const medir = () => setCabe(el.scrollWidth - el.clientWidth <= 1);
    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(el);
    /* Os cartões também: a largura deles é `clamp(…, 16vw, …)`, e num painel de
       largura fixa (a prévia do Estúdio) a janela pode não mudar quando eles
       mudam. */
    for (const filho of el.children) observador.observe(filho);
    return () => observador.disconnect();
  }, [quantos]);

  return [trilho, cabe];
}

function Prateleira({
  titulo,
  cartao,
  children,
  regiao,
  rotulo,
  refId,
  colada = false,
  emGrade = false,
}: {
  titulo: string;
  proporcao: string;
  /** O formato do cartão desta faixa (S4): vira variáveis CSS na seção; ausente = nada. */
  cartao?: CartaoGravado;
  children: React.ReactNode;
  /** Só o Estúdio lê. Fora dele, `data-` é HTML morto. */
  regiao?: string;
  rotulo?: string;
  refId?: string;
  /**
   * Encosta na seção de cima.
   *
   * A primeira prateleira depois do herói tinha 40px de `pt-10` mais os 32px do
   * `pt-8` da saudação: 72px de nada entre o botão `Começar` e o primeiro cartão
   * de "continue de onde parou" — o vão que o dono chamou de grande demais. Entre
   * duas prateleiras os 40px estão certos, porque ali eles separam duas coisas do
   * mesmo peso; abaixo do herói eles separam a chamada da ação, que é onde a
   * página não deve respirar.
   */
  colada?: boolean;
  /**
   * Quebra em GRADE em vez de correr num trilho — ordem do dono de 24/09/2026.
   *
   * > *«o continue onde parou está crescendo e vazando na página, ele deveria ter
   * > um teto e exibir as últimas»*
   *
   * ## O trilho está certo para o catálogo e errado para esta faixa
   *
   * O catálogo é aberto: um curso novo é sempre possível, a fila não tem fim
   * natural, e cortar o último cartão na borda é o que diz «há mais, arraste».
   * A retomada é o oposto — ela é **fechada por regra** (`TETO_DE_RETOMADAS = 5`,
   * uma aula por curso). Num trilho, esse teto fica invisível: o aluno vê o
   * cartão cortado e conclui que a lista não acaba, que foi exactamente a leitura
   * do dono.
   *
   * ## A medida que decide
   *
   * O ladrilho é `clamp(300px, 28vw, 416px)`. A 1920 ele trava em 416, e
   * 5 × 416 + 4 × 16 = **2.144px** contra ~1.856 de área útil: o quinto cartão
   * nasce cortado com apenas cinco itens — e o aluno medido tinha cinco.
   *
   * Em grade, os mesmos seis cabem em duas fileiras de três a 1920 e uma coluna
   * no telemóvel, **sem um pixel de rolagem lateral em largura nenhuma**. O teto
   * passa a ser visível: acabou a fileira, acabou a lista.
   *
   * Não vira grade para todas: as outras faixas continuam trilho, porque para
   * elas o corte na borda é informação verdadeira.
   */
  emGrade?: boolean;
}) {
  const [trilho, cabe] = useCabeNoTrilho(Children.count(children));

  return (
    <section
      className={colada ? 'pt-4' : 'pt-10'}
      style={variaveisDoCartao(cartao) as React.CSSProperties | undefined}
      data-vst-regiao={regiao}
      data-vst-rotulo={rotulo}
      data-vst-ref={refId}
    >
      <h2 className="px-6 pb-4 text-xl font-semibold tracking-tight sm:px-8">{titulo}</h2>
      {/* Rolagem horizontal com snap: no celular é o gesto natural, e no desktop
          o trackpad faz o mesmo. Sem setas, que só existem para mouse antigo.
          `pt-1 pb-4` dá ao cartaz espaço para subir no hover sem ser cortado
          pelo `overflow-x` — sem isso, o topo da capa some ao passar o mouse. */}
      <div
        ref={trilho}
        /* Em grade nada rola de lado — e o atributo diz isso, para a cerca poder
           medir em vez de confiar na classe. */
        data-vst-rola={emGrade || cabe ? 'nao' : 'sim'}
        data-vst-grade={emGrade ? 'sim' : undefined}
        data-vst-trilho={refId ?? undefined}
        className={
          emGrade
            ? /*
               * ── CINCO NUMA FILEIRA, POR CONSTRUÇÃO (ordem do dono, 24/09/2026) ──
               *
               * > *«vc colocou duas linhas para continue onde parou e eu quero só
               * > uma»* … *«vc pode diminuir a largura dos cards e colocar 5 na
               * > mesma linha»*
               *
               * Colunas de `1fr`, e não largura fixa no ladrilho: com `clamp(300px,
               * 28vw, 416px)` o número de cartões por fileira era CONSEQUÊNCIA da
               * largura da janela — quatro a 1920, três a 1440 —, e é por isso que
               * o teto de seis dava duas fileiras. Com `grid-cols-5` o cartão
               * ocupa o que sobra dividido por cinco (358px a 1920, 230px a 1280),
               * e a fileira é uma só **porque o teto também é cinco**.
               *
               * A escada abaixo de `xl` não é enfeite: cinco colunas a 768px dariam
               * 128px por cartão, que não cabe miniatura e título. Lá ele empilha.
               */
              'grid grid-cols-1 gap-4 px-6 pb-4 pt-1 sm:grid-cols-2 sm:px-8 lg:grid-cols-3 xl:grid-cols-5'
            : `flex gap-4 overflow-x-auto px-6 pb-4 pt-1 [scrollbar-width:none] sm:px-8 [&::-webkit-scrollbar]:hidden ${cabe ? '' : 'snap-x snap-mandatory'}`
        }
      >
        {children}
      </div>
    </section>
  );
}

/**
 * O cartaz que se mexe — quando, e por quanto tempo.
 *
 * ## O gesto muda com o aparelho, e o motivo não é estético
 *
 * No computador existe o **mouse parado sobre uma coisa**, que é uma declaração
 * de interesse tão clara quanto um clique. No celular não existe: o dedo ou toca
 * (e aí navega) ou passa. Então lá o sinal é outro — **o cartaz estar na tela**,
 * pelo `IntersectionObserver`, que é o equivalente honesto de "esta é a que a
 * pessoa está olhando".
 *
 * Quem decide qual dos dois vale é `(hover: hover)`, e não a largura da janela:
 * há tablet largo sem mouse e há laptop estreito com mouse, e a pergunta certa é
 * sobre o apontador, não sobre pixels.
 *
 * ## O teclado dispara o mesmo que o mouse
 *
 * `onFocus`/`onBlur` no mesmo elemento que ouve o mouse. Sem isso, quem navega
 * por `Tab` — que é quem mais depende de a interface responder ao foco — nunca
 * veria o recurso existir. Não é um caso de acessibilidade à parte: é o mesmo
 * evento, com outro dispositivo.
 *
 * ## O que garante que não pisca nem empurra nada
 *
 * A prévia entra como `<img>` **posicionada por cima**, dentro da mesma caixa
 * que já tem `aspect-ratio` e `overflow-hidden`. Ela não participa do fluxo, não
 * tem tamanho próprio e não pode mudar a altura de coisa nenhuma — o cartaz
 * parado continua exatamente onde estava, embaixo. É também o que faz `parar()`
 * ser instantâneo e sem buraco: tira-se o `src`, e o que aparece é a imagem que
 * nunca saiu.
 */
function useCartazAnimado(areaSlug: string, curso: CursoDaVitrine) {
  const caixa = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);

  /*
   * Decidido UMA vez, na montagem. As duas chaves não mudam durante uma visita,
   * e reavaliá-las a cada quadro custaria um `matchMedia` por cartaz por render.
   */
  const [permitido] = useState(
    () => Boolean(curso.previa && curso.capa) && podeAnimar(lerAmbienteDoMovimento()),
  );

  const ligar = useCallback(() => {
    if (!permitido) return;
    /* O maestro avisa quem estava tocando. Ver `capa-animada.ts`: um de cada
     * vez, na página inteira — cinco laços numa faixa é o que esquenta o
     * aparelho, e a pessoa está olhando um. */
    pedirAVez(curso.id, () => setSrc(null));
    setSrc(enderecoDaPrevia(areaSlug, curso.id));
  }, [permitido, curso.id, areaSlug]);

  const parar = useCallback(() => {
    devolverAVez(curso.id);
    setSrc(null);
  }, [curso.id]);

  useEffect(() => {
    if (!permitido) return;
    /* Onde há apontador, quem manda é o apontador — e o observador nem é criado. */
    if (typeof matchMedia === 'function' && matchMedia('(hover: hover)').matches) return;
    const el = caixa.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    /* 0,9 e não 0,5: numa tela de 390px cabem dois cartazes e meio, e um limiar
     * baixo faria os dois pedirem a vez a cada rolagem — o maestro resolveria,
     * mas às custas de um pedido de rede por cartaz que passou. */
    const observador = new IntersectionObserver(
      ([entrada]) => (entrada?.isIntersecting ? ligar() : parar()),
      { threshold: 0.9 },
    );
    observador.observe(el);
    return () => {
      observador.disconnect();
      parar();
    };
  }, [permitido, ligar, parar]);

  /* Sair da tela sem devolver a vez deixaria a página inteira sem cartaz
   * animado, porque o maestro ainda acharia que este está tocando. */
  useEffect(() => () => devolverAVez(curso.id), [curso.id]);

  return { caixa, src, ligar, parar, permitido };
}

/**
 * O cartão de um MÓDULO ou de uma AULA posto numa prateleira (leva A1).
 *
 * A mesma caixa, a mesma proporção e o mesmo levantar do cartaz de curso — o
 * que muda é o que está escrito: uma linha em cima diz de que curso ele veio
 * («Módulo · Openclaw e Hermes», «Vídeo · Claude Code»), e a de baixo é a
 * legenda que o servidor já resolveu (`legendaDoItem`, no core). Sem capa
 * própria nem do curso, a marca do curso DONO (V7): o cartão continua com a
 * cara de onde pertence. O clique leva à aula — a primeira do módulo, ou ela
 * mesma —, e é a sala quem diz se ela está trancada.
 */
function CapaDeItem({
  item,
  areaSlug,
  proporcao,
  tamanho,
}: {
  item: Extract<ItemDaPrateleira, { tipo: 'modulo' | 'aula' }>;
  areaSlug: string;
  proporcao: string;
  tamanho: TamanhoDeCapa | undefined;
}) {
  const marca = marcaDoCurso(item.cursoId, item.cursoTitulo);
  const origem = item.tipo === 'modulo' ? 'Módulo' : rotuloDoTipo(item.aulaTipo);

  return (
    <Link
      to={caminhoDaArea(areaSlug, item.para)}
      data-vst-regiao="capa"
      data-vst-rotulo={item.titulo}
      data-vst-item={`${item.tipo}:${item.id}`}
      data-vst-cartao={`${item.tipo}:${item.id}`}
      aria-label={`${item.titulo} — ${origem.toLowerCase()} de ${item.cursoTitulo}`}
      className="group shrink-0 snap-start"
      style={{ width: larguraDaCapa(proporcao as never, tamanho) }}
    >
      <div
        className="relative flex items-end overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.02] motion-reduce:transform-none"
        style={{
          aspectRatio: aspectoDaCapa(proporcao),
          background: item.capa ? undefined : marca.fundo,
          borderRadius: 'var(--cartao-raio, var(--raio))',
          border: 'var(--cartao-borda, none)',
          boxShadow: 'var(--cartao-sombra, none)',
        }}
      >
        {item.capa && (
          <CapaEmImagem
            src={item.capa}
            srcset={item.capaSrcset}
            sizes={sizesDaCapa(proporcao as never, tamanho)}
            largura={item.capaLargura}
            altura={item.capaAltura}
            prioridade="cartaz"
          />
        )}
        {!item.capa && <Monograma iniciais={marca.iniciais} className="text-4xl" />}
        {/*
         * O carimbo da AMOSTRA — e só ele: sem véu, sem preto e branco.
         *
         * Esta aula abre (desde `73a10fd2c`, por quatro rotas de leitura), e o
         * véu é a linguagem do que NÃO abre. O que o carimbo responde é a
         * pergunta que o cartaz levanta — *por que está aqui uma aula de um
         * curso que não é meu?* —, e a resposta é: é uma amostra. Ver
         * `SeloDeAmostra`.
         */}
        {item.amostra && <SeloDeAmostra />}
      </div>
      <p
        className="pt-2.5 text-[11px] font-medium uppercase tracking-[0.12em]"
        style={{ color: 'var(--texto-suave)' }}
      >
        {origem} · {item.cursoTitulo}
      </p>
      <p className="pt-0.5 text-[15px] font-medium leading-snug">{item.titulo}</p>
      {item.linha && (
        <p className="text-xs" style={{ color: 'var(--texto-suave)' }}>
          {item.linha}
        </p>
      )}
    </Link>
  );
}

/**
 * ── A PILHA: COMO UM CARTAZ 3/4 DIZ «ISTO É UMA COLEÇÃO» ────────────────────
 *
 * A pergunta desta obra, por extenso: *como é que se lê «isto é uma COLEÇÃO»
 * num cartaz, a três metros, sem usar o canto dos carimbos e sem acrescentar
 * uma linha de altura?*
 *
 * ## Por que o canto dos carimbos está fora de questão
 *
 * `PlacaNoCartaz` é `absolute left-2 top-2`, e as cinco espécies de carimbo
 * vivem **todas no mesmo pixel** — elas nunca colidem porque são mutuamente
 * exclusivas por construção (acesso → hora marcada → pré-requisito). «É trilha»
 * não é exclusivo de nenhuma delas: uma trilha pode estar agendada **e**
 * trancada ao mesmo tempo. Um sexto carimbo ali teria de empilhar duas chapas ou
 * disputar a vaga — e quem perde a disputa é sempre o que diz **o que a coisa
 * é**, que é o único sinal que nunca muda.
 *
 * ## A tese: é uma pilha, e o que a denuncia é a SILHUETA
 *
 * É a mesma tese que `OF29` aplicou ontem na tela de downloads, pela mesma
 * queixa — *«a seção que agrupa os arquivos se confunde com o próprio
 * arquivo»* —, e com a mesma lição: **o que separa duas espécies de coisa não é
 * o tamanho da letra, é a forma**. Lá foi a lombada ao lado do grupo; aqui são
 * duas arestas deslocadas atrás do cartaz.
 *
 * A silhueta é o único sinal que sobrevive a três metros, porque é
 * pré-atentiva: numa fileira de retângulos iguais, o olho acha o que não é
 * retângulo antes de ler nada. E custa **zero pixel de altura** — a caixa que
 * declara a proporção é a da PILHA, e é a arte que recua para dentro dela.
 *
 * ## A tinta é uma mistura de `--texto`, e isso é medição, não gosto
 *
 * A proposta de partida era desenhar as arestas em `--superficie`, que é a cor
 * de cartão desta casa. **Medido: `--superficie` sobre `--fundo` dá 1,09:1** no
 * tema de fábrica (`#16161d` sobre `#0b0b0f`) e 1,13:1 num tema claro — e uma
 * aresta assim não existe a três metros nem a trinta centímetros. É da mesma
 * ordem do 1,07 que a sonda de tinta desta casa deixou passar por anos noutro
 * sítio.
 *
 * O único par que a área **garante** legível é `--texto` contra o fundo dela
 * (C4), e por isso a aresta é uma mistura dele. As percentagens não são
 * redondas por acaso: são o mínimo que mantém ≥ 3:1 (o piso de elemento gráfico)
 * nos dois extremos — tema escuro de fábrica e tema claro de cliente —, e a
 * conta corre em `o-cartao-da-trilha-nao-empilha-carimbos.test.tsx`.
 *
 * ## E a pilha NÃO recebe o véu do cartaz fechado
 *
 * O véu é a linguagem de «isto não abre», e ele veste **a arte do tutor** —
 * tira-lhe a cor e baixa-a um degrau. A pilha não é arte: é a espécie do cartão,
 * desenhada. E a espécie não muda quando a porta fecha: uma trilha trancada
 * continua a ser uma trilha, e é justamente no cartão fechado que o carimbo está
 * ocupado a dizer outra coisa. Apagar a pilha ali deixaria a única espécie da
 * prateleira sem nenhum sinal de identidade — que é o defeito que esta peça
 * existe para fechar.
 */
const DEGRAU_DA_PILHA: Record<TamanhoDeCapa, number> = { pequeno: 5, medio: 6, grande: 7 };

/** A mistura de `--texto` de cada aresta. Ver a conta no cabeçalho da pilha. */
const TINTA_DA_ARESTA: Record<1 | 2, string> = {
  /* A de trás encosta na PÁGINA: é ela que tem de bater o piso de 3:1. */
  2: 'color-mix(in srgb, var(--texto) 48%, var(--fundo))',
  /* A do meio encosta na de trás, e o que ela precisa é de se separar DELA. A
     escada é monótona — página → 48% → 92% → a arte — porque a direção que
     separa muda de tema: num tema escuro afastar-se da página é clarear, num
     claro é escurecer, e só a monotonia serve os dois com a mesma conta. */
  1: 'color-mix(in srgb, var(--texto) 92%, var(--fundo))',
};

/**
 * Uma aresta da pilha — o canto de um cartaz que está por baixo.
 *
 * Deslocada para **cima e para a direita**, e nunca para baixo: a borda de baixo
 * do cartaz é onde o título se apoia, e uma aresta ali abriria um vão entre a
 * arte e a legenda que faria o texto parecer desencostado do cartão.
 */
function ArestaDaPilha({ grau, degrau }: { grau: 1 | 2; degrau: number }) {
  /*
   * Toda aresta tem o MESMO tamanho do volume da frente — (W − 2·degrau) ×
   * (H − 2·degrau) —, e o que muda é só onde ela está: cada grau sobe e anda
   * para a direita um degrau. Daí as duas contas espelhadas.
   *
   * (A primeira versão usava `degrau × grau` nos quatro lados e dava à aresta
   * do meio um tamanho MAIOR que a de trás: ela cobria-a inteira, e as duas
   * mediam a mesma tinta no canvas. Foi a sonda que o apanhou, não o olho.)
   */
  const recuado = `${grau * degrau}px`;
  const avancado = `${(2 - grau) * degrau}px`;
  return (
    <span
      aria-hidden
      data-vst-lombada={grau}
      className="absolute"
      style={{
        top: avancado,
        right: avancado,
        left: recuado,
        bottom: recuado,
        background: TINTA_DA_ARESTA[grau],
        borderRadius: 'var(--cartao-raio, var(--raio))',
      }}
    />
  );
}

/**
 * O cartão de uma TRILHA posta numa prateleira, misturada com cursos.
 *
 * ## Por que não é um ramo dentro de `CapaDeItem`
 *
 * Porque a identidade inteira daquele componente é «um item que PERTENCE a um
 * curso»: ele imprime `{origem} · {item.cursoTitulo}` e o rótulo diz «módulo de
 * X». O `cursoTitulo` de uma trilha seria o curso-sombra — e imprimi-lo vazaria,
 * no cartão mais visível da vitrine, exactamente a linha que o curso-sombra
 * existe para esconder.
 *
 * ## Por que não é uma quinta saída de `CapaDeCurso`
 *
 * Porque aquela função tem 450 linhas, quatro saídas e o cartaz animado, e a
 * trilha não tem laço animado nem página de venda. Uma quinta saída e dois
 * `if (!éTrilha)` é como o cartaz do curso e o da trilha divergem no primeiro
 * conserto.
 *
 * ## O que ela PARTILHA, e é o que a faz pousar na mesma fileira
 *
 * A caixa com `aspectRatio` e `width` do core, as variáveis `--cartao-*`,
 * `CapaEmImagem`, `Monograma`, `VeuDoCartaz`, os dois carimbos — e, debaixo do
 * cartaz, **a mesma estrutura do cartão de curso**: `<Barra>`, o título, uma
 * linha. Não é parecida: é a mesma, na mesma ordem, com as mesmas classes. É
 * por construção que as duas espécies têm a mesma altura, e não por coincidência
 * de corpo de letra.
 *
 * ── A SOBRANCELHA FOI DERRUBADA, E O RETRATO É QUE A DERRUBOU ───────────────
 *
 * A proposta de partida punha uma linha acima do título — `Trilha · 4 cursos` —
 * no mesmo lugar e na mesma classe em que `CapaDeItem` imprime a origem.
 * Medido em `retratos-do-cartao-da-trilha/antes` numa fileira mista a 1440 px:
 *
 * | espécie | onde o título pousa |
 * |---|---|
 * | curso em andamento | 336,39 px |
 * | curso sem progresso | 326,39 px |
 * | aula | 352,89 px |
 * | módulo | 369,39 px |
 *
 * **A sobrancelha é o que faz o cartão de módulo quebrar a fileira** — 43 px de
 * desvio contra o curso, e no telemóvel ela ainda parte em duas linhas. Dar a
 * mesma linha à trilha seria pôr a espécie nova no sítio onde o defeito já mora.
 * A identidade mudou-se para a linha que já existe, como primeiro segmento.
 *
 * (A fileira só *parece* alinhada porque o trilho é `flex` sem `align-items`, e
 * o `stretch` estica o cartão mais curto até à altura do mais alto: as quatro
 * caixas mediam 408,02 px **enquanto os quatro títulos pousavam em sítios
 * diferentes**. A caixa não é o oráculo; onde a tinta pousa é.)
 */
function CapaDeTrilha({
  item,
  areaSlug,
  proporcao,
  tamanho,
}: {
  item: ItemDeTrilha;
  areaSlug: string;
  proporcao: string;
  tamanho: TamanhoDeCapa | undefined;
}) {
  const t = useT();
  /* A marca sai do id da TRILHA, e não de curso nenhum: sem capa própria, o
     monograma é o dela. Dar-lhe a marca de um dos cursos de dentro seria o
     cartão da coleção a vestir-se de um dos volumes. */
  const marca = marcaDoCurso(item.id, item.titulo);
  /*
   * ── A PRECEDÊNCIA, INTEIRA: ACESSO → HORA MARCADA → PRÉ-REQUISITO ────────
   *
   * A MESMA escada do cartaz de curso, e pelas mesmas razões, que o servidor
   * escreve por extenso em `app-estante.ts`: o acesso é a razão mais de fora que
   * existe, e dizer «Agendado» ou «pede Fundamentos» sobre uma trilha que não é
   * desta pessoa é mandá-la cumprir o passo errado — ela não tem o que agendar
   * nem o que cumprir. O que ela tem para fazer está na página de venda.
   *
   * O servidor já concorda (`comoVenda` vem com `tranca: null` de propósito), e
   * a porta fecha-se também deste lado porque o `dist` do aluno e a API podem
   * ser de levas diferentes por uns minutos. Passados por `lerLancamento` e
   * `lerTranca`, e não lidos campo a campo, pela mesma razão.
   *
   * **São estas três linhas que garantem o teto de um carimbo.** As espécies
   * excluem-se por construção, e não por quem desenha se lembrar — e o caso que
   * o prova é a trilha que está agendada E sem acesso ao mesmo tempo.
   */
  const comoVenda = Boolean(item.comoVenda);
  const lancamento = comoVenda ? null : lerLancamento(item.lancamento);
  const tranca = comoVenda || lancamento ? null : lerTranca(item.tranca);
  /* `fechado` é «não abre em sítio nenhum». O cartaz de venda NÃO é fechado: ele
     levanta ao rato e é um elo, porque tem para onde levar. É a mesma linha que
     separa as duas famílias no cartaz de curso. */
  const fechado = Boolean(tranca || lancamento);
  /* O aviso da tranca, aberto pelo clique no cartaz. Estado DESTE cartão e não
     da prateleira: dois cartazes trancados na mesma fileira são duas perguntas
     diferentes, e um estado partilhado faria o segundo clique responder pelo
     primeiro. Ver `AvisoDaTranca`. */
  const [avisando, setAvisando] = useState(false);
  /*
   * ── O DESTINO DE FORA E O DESTINO DE DENTRO, PELA FORMA DO ENDEREÇO ──────
   *
   * A mesma régua de `CapaDeCurso` (`0461`), e de propósito a mesma: o fato é o
   * mesmo fato. `http(s)://` é a página do TUTOR, noutro domínio — aba nova e a
   * seta que sai; qualquer outra coisa é uma rota DESTE app, e ali as duas
   * mentiriam: abrir aba nova recarrega o pacote inteiro e perde a prateleira, e
   * a seta diagonal anuncia uma saída que não acontece.
   *
   * Inventar aqui uma segunda régua — um campo «é externo», ou a trilha a tratar
   * sempre como interno — seria dois fatos a dizer o que um já diz, e o dia em
   * que discordassem o cartaz abriria aba nova para uma rota do próprio app.
   */
  const destinoDeFora = typeof item.oferta === 'string' && /^https?:\/\//i.test(item.oferta);

  const degrau = DEGRAU_DA_PILHA[tamanho ?? 'medio'];
  const recuo = degrau * 2;

  const pilha = (
    <div
      data-vst-pilha=""
      /* Quem levanta ao rato é a PILHA inteira, e não o cartaz de cima: tirar um
         volume de uma pilha levanta a pilha. Fechada não levanta, pela mesma
         razão do curso — o levantar é a promessa do clique, e aqui não há. */
      className={
        fechado
          ? 'relative'
          : 'relative transition-transform duration-300 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.02] motion-reduce:transform-none'
      }
      style={{ aspectRatio: aspectoDaCapa(proporcao) }}
    >
      {/* De trás para a frente: a ordem no DOM é a ordem da profundidade. */}
      <ArestaDaPilha grau={2} degrau={degrau} />
      <ArestaDaPilha grau={1} degrau={degrau} />
      <div
        data-vst-volume=""
        /* O volume da frente é, peça por peça, a caixa do cartaz de curso — o
           que muda é que ela está recuada para dentro da pilha em vez de ocupar
           a caixa inteira. `inset` com o fundo e a esquerda a zero: a arte
           encosta onde o título se apoia. */
        className="absolute flex items-end overflow-hidden"
        style={{
          inset: `${recuo}px ${recuo}px 0 0`,
          background: item.capa ? undefined : marca.fundo,
          borderRadius: 'var(--cartao-raio, var(--raio))',
          border: 'var(--cartao-borda, none)',
          boxShadow: 'var(--cartao-sombra, none)',
        }}
      >
        {item.capa && (
          <CapaEmImagem
            src={item.capa}
            srcset={item.capaSrcset}
            sizes={sizesDaCapa(proporcao as never, tamanho)}
            largura={item.capaLargura}
            altura={item.capaAltura}
            prioridade="cartaz"
            /* Fechada, a arte perde a cor e não a recupera. Sem acesso, o preto
               e branco é o mesmo e a classe é outra: lá a cor VOLTA ao passar o
               rato, porque aquele cartaz leva à página de venda e a arte é o que
               ela está a vender. Ver `.vst-arte-sem-acesso`, em `index.css`. */
            className={fechado ? 'vst-arte-fechada' : comoVenda ? 'vst-arte-sem-acesso' : ''}
          />
        )}
        {!item.capa && (
          <Monograma
            iniciais={marca.iniciais}
            className={`text-4xl${fechado ? ' vst-arte-fechada' : comoVenda ? ' vst-arte-sem-acesso' : ''}`}
          />
        )}
        {/* O véu veste a ARTE e fica dentro do volume — ver o cabeçalho da
            pilha para a razão de ele não cobrir as arestas. */}
        {fechado && (
          <VeuDoCartaz
            /* As duas espécies fechadas falam dentro da capa — a mesma escada
               do cartaz de curso, e pela mesma ordem do dono de 14/10/2026. A
               trilha herda isto por construção: é esta linha, e não uma cópia
               do desenho, que a mantém na fileira. */
            estado={
              lancamento ? (
                <EstadoNoCartaz lancamento={lancamento} />
              ) : tranca ? (
                <MotivoNoCartaz tranca={tranca} areaSlug={areaSlug} />
              ) : undefined
            }
          />
        )}
        {/*
         * O véu do cartaz SEM ACESSO — a mesma peça, com o glifo trocado.
         *
         * Trocado porque o fato é outro: o cadeado responde «isto não abre», e
         * este cartaz abre — noutro endereço. O glifo é o que o elo FAZ,
         * desenhado: a seta que sai para a página do tutor, a que avança para
         * uma rota deste app.
         */}
        {comoVenda && <VeuDoCartaz gesto={destinoDeFora ? <SetaQueSai /> : <SetaQueLeva />} />}
        {tranca && <SeloDeOrdem />}
        {lancamento && <SeloDeLancamento />}
        {comoVenda && <SeloSemAcesso />}
      </div>
    </div>
  );

  /*
   * A legenda, e por que ela é UMA linha com dois pesos.
   *
   * `--texto` na espécie e `--texto-suave` na conta: o par é garantido legível
   * pela área (C4), ao contrário de `--destaque`, que é livre e pode ser um
   * verde-limão. É a mesma razão por que o `Monograma` não usa o destaque.
   *
   * `truncate` porque esta linha é mais comprida do que a do curso e o cartão
   * mais estreito desta casa tem 130 px: sem ele, «Trilha · 4 cursos · 18 h»
   * parte em duas e a trilha passa a ser o cartão que **acrescenta** altura à
   * fileira, que é precisamente o que esta obra não pode fazer. Truncada, a
   * espécie sobrevive ao corte porque vem primeiro.
   */
  /*
   * ── E O QUE A SEGUNDA METADE DIZ MUDA COM A POSSE (07/10/2026) ───────────
   *
   * Sem acesso, a conta sai e entra a posse: **«Trilha · Você ainda não tem»**,
   * a espécie em `--texto` e a posse em `--destaque`, que é onde o cartaz de
   * curso já põe essa frase desde 18/09.
   *
   * **Por que a conta sai.** É a regra que o cartaz de curso escreveu e que vale
   * igual aqui: a linha do cartão bloqueado responde a POSSE, não a tamanho, e
   * pendurar «· 18 h» nela anunciaria o tamanho do que a pessoa não tem. Quem
   * quer vender o tamanho tem a página de venda, para onde este cartão leva.
   *
   * **Por que a espécie FICA.** Porque ela não é tamanho: é o que está à venda.
   * O cartaz de curso pode dar-se ao luxo de só dizer a posse, porque ninguém
   * duvida de que um cartaz de curso é um curso. Aqui a pergunta «o que é isto
   * que eu não tenho?» é a primeira que a pessoa faz — e é o cartão de venda, de
   * todos, aquele em que respondê-la mais importa.
   */
  const legenda = (
    <p
      className="truncate text-xs"
      style={{ color: 'var(--texto-suave)' }}
      data-vst-trilha-linha=""
    >
      <span style={{ color: 'var(--texto)' }}>{t('trilha.titulo')}</span>
      {comoVenda ? (
        <>
          {' · '}
          <span style={{ color: 'var(--destaque)' }}>{t('vitrine.voceAindaNaoTem')}</span>
        </>
      ) : item.linha ? (
        ` · ${item.linha}`
      ) : (
        ''
      )}
    </p>
  );

  const pele = {
    'data-vst-regiao': 'trilha',
    'data-vst-rotulo': `trilha · ${item.titulo}`,
    /* O slug, e não o uuid — a mesma régua de `TrilhasDaArea`: ele já é
       público (está na URL do aluno) e é único na área, que é o escopo em que o
       editor procura. */
    'data-vst-ref': item.slug,
    'data-vst-trilha': item.id,
    'data-vst-cartao': `trilha:${item.id}`,
    className: 'group shrink-0 snap-start',
    style: { width: larguraDaCapa(proporcao as never, tamanho) },
  } as const;

  /*
   * ── O QUE FICA DEBAIXO DO CARTAZ, ESCRITO UMA VEZ SÓ ─────────────────────
   *
   * O cartaz de curso escreve este bloco **quatro vezes**, uma por saída, e é
   * por isso que as suas quatro espécies já divergiram em 6 px sem ninguém
   * decidir. Aqui ele é um só: o que muda entre as saídas é a CASCA — um `<a>`,
   * um `<Link>` ou um `<div>` —, e nunca o que está escrito.
   *
   * A `Barra` sai no cartaz de venda pela razão da própria barra: um progresso
   * de 0% sobre uma trilha que não é desta pessoa mede o que ela não fez num
   * conjunto que ela não tem.
   *
   * A `Barra` do cartaz de CURSO, e não a `BarraDaTrilha`: numa fileira a barra
   * tem de ser a mesma dos vizinhos, senão duas medidas iguais aparecem com duas
   * espessuras a 16 px uma da outra. A da trilha vive na seção das trilhas, onde
   * não há curso ao lado.
   */
  const abaixo = (
    <>
      {!fechado && !comoVenda && <Barra percent={item.percentual} />}
      <p className="pt-2.5 text-[15px] font-medium leading-snug">{item.titulo}</p>
      {/*
       * ── A LEGENDA FICA NAS TRÊS ESPÉCIES (14/10/2026) ──────────────────────
       *
       * Fechada — por hora marcada ou por pré-requisito —, a frase está DENTRO
       * da capa, e aqui fica a legenda de sempre. A espécie continua dita pela
       * PILHA, que o véu não apaga.
       *
       * **O que isto derruba.** Até hoje a trilha trancada trocava «Trilha · 2
       * cursos» pelo motivo, e o argumento era que *«o tamanho do que a pessoa
       * não alcança»* não se anuncia. Ele era bom quando o motivo ficava aqui
       * em baixo — sem ele, o cartão não dizia nada e a conta ficava sozinha a
       * medir o inalcançável. Com o motivo na capa a leitura inverte-se: a
       * conta deixa de ser a única coisa escrita e passa a ser o tamanho do que
       * a pessoa está a caminho de abrir, com o porquê dito por extenso 8px
       * acima dela.
       *
       * **E é o que a põe na fileira.** Um cartão fechado sem linha nenhuma
       * desnivela a prateleira tanto quanto um com três — para cima num caso,
       * para baixo no outro. A medição de então continua a valer e agora vale
       * para todos: nenhuma espécie leva moldura de `pt-1.5`, que eram os 6 px
       * que faziam o agendado pedir 379,02 px onde o aberto pede 373,02.
       */}
      {legenda}
    </>
  );

  /*
   * ── O CARTAZ DE VENDA DE FORA: ABA NOVA, E A SETA QUE O DIZ ──────────────
   *
   * `<a>` e não `<Link>` porque o endereço é de outro sítio: dá-lo ao roteador
   * faria o React Router procurar uma rota do app do aluno para `https://…`. Aba
   * nova porque a área de membros é um lugar em que a pessoa ESTÁ e a página de
   * venda é uma consulta; `noopener` porque o destino é texto que o tutor
   * escreveu num campo.
   */
  if (comoVenda && item.oferta && destinoDeFora) {
    return (
      <a
        {...pele}
        href={item.oferta}
        target="_blank"
        rel="noopener noreferrer"
        data-vst-sem-acesso="cartao"
        aria-label={`${rotuloDaTrilha(item)} — abre a página de venda`}
      >
        {pilha}
        {abaixo}
      </a>
    );
  }

  /* Fechada não é `<Link>`, pela razão de sempre: o servidor recusa o corpo lá
     dentro, e um link seria a tela a oferecer o que a API fecha. */
  if (fechado) {
    /* Trancada, a casca é um `<button>` que abre o aviso — a MESMA escada do
       cartaz de curso, e pela mesma ordem do dono de 14/10/2026 à tarde. A
       razão de a trilha herdar isto por uma linha, e não por uma cópia do
       desenho, é a de sempre: duas implementações divergem no primeiro
       conserto, e a espécie em que isto se perderia é aquela que a pessoa não
       consegue abrir — a que ninguém vai experimentar.

       Agendada, continua um `<div>` inerte: «ainda não é hora» não tem gesto
       nenhum do outro lado. Ver `CapaDeCurso`, onde a distinção está medida. */
    if (tranca) {
      return (
        <>
          <button
            {...pele}
            type="button"
            data-vst-tranca="cartao"
            aria-haspopup="dialog"
            className="group shrink-0 cursor-pointer snap-start text-left"
            onClick={() => setAvisando(true)}
          >
            {pilha}
            {abaixo}
          </button>
          {avisando && (
            <AvisoDaTranca
              especie="trilha"
              tranca={tranca}
              areaSlug={areaSlug}
              aoFechar={() => setAvisando(false)}
            />
          )}
        </>
      );
    }
    return (
      <div {...pele} data-vst-lancamento={lancamento ? 'cartao' : undefined}>
        {pilha}
        {abaixo}
      </div>
    );
  }

  /*
   * O elo de dentro — e são dois casos na mesma saída, de propósito.
   *
   * A venda cujo endereço é uma rota DESTE app (a página que o Vestigo serve
   * quando o tutor não declarou a dele) e a trilha que é mesmo da pessoa levam
   * ambas a um `<Link>` sem aba nova. O que as separa é para ONDE, e é a única
   * coisa que as separa: o carimbo, o véu e a legenda já disseram o resto.
   *
   * E a venda **sem endereço nenhum** cai aqui também, a levar a `/trilha/…`:
   * continua a dizer «não incluído» pelo carimbo, porque isso é verdade,
   * e não inventa um destino que não lhe deram.
   */
  const vendaDeDentro = comoVenda && item.oferta;
  return (
    <Link
      {...pele}
      to={caminhoDaArea(areaSlug, vendaDeDentro ? item.oferta! : item.para)}
      data-vst-sem-acesso={comoVenda ? 'cartao' : undefined}
      aria-label={
        vendaDeDentro
          ? `${rotuloDaTrilha(item)} — abre a página desta trilha`
          : rotuloDaTrilha(item)
      }
    >
      {pilha}
      {abaixo}
    </Link>
  );
}

/**
 * O rótulo de voz: a espécie entra por extenso, porque a pilha é silenciosa.
 *
 * E quem ouve a tela recebe **a mesma frase que se vê**, incluindo a troca do
 * segundo segmento no cartaz de venda: a conta sai e entra a posse. Repetir ali
 * «4 cursos · 18 h» seria o rótulo a anunciar o tamanho do que a pessoa não tem
 * — exactamente o que a linha visível recusa fazer, e por isso mesmo o sítio
 * onde a recusa passa despercebida.
 */
function rotuloDaTrilha(item: ItemDeTrilha): string {
  if (item.comoVenda) return `${item.titulo} — trilha, você ainda não tem`;
  return `${item.titulo} — trilha${item.linha ? `, ${item.linha}` : ''}`;
}

function CapaDeCurso({
  curso,
  areaSlug,
  proporcao,
  tamanho,
}: {
  curso: CursoDaVitrine;
  areaSlug: string;
  proporcao: string;
  /** O cartaz que o tutor escolheu. Ver `larguraDaCapa`, no core. */
  tamanho: TamanhoDeCapa | undefined;
}) {
  const t = useT();
  const marca = marcaDoCurso(curso.id, curso.title);
  const animado = useCartazAnimado(areaSlug, curso);
  /*
   * ── O CURSO É DELA E ESPERA OUTRO (23/09/2026) ───────────────────────────
   *
   * A ordem do dono, de 18/09: *«na vitrine é exibido trancado, na trilha
   * também»*. A trilha já o desenhava desde então; a vitrine não, e o desfecho
   * era o pior possível — o aluno clicava no cartaz e levava com a porta na
   * cara (`exigirAula` recusa o corpo), sem aviso nenhum antes do clique.
   *
   * Passado por `lerTranca` e não lido campo a campo: o `dist` do aluno e a API
   * podem ser de levas diferentes por uns minutos, e um campo em falta não pode
   * apagar a prateleira inteira.
   */
  /*
   * ── A DATA MANDA NA DEPENDÊNCIA (24/09/2026) ─────────────────────────────
   *
   * Um curso pode ter as duas coisas: estrear em março **e** pedir «Fundamentos»
   * antes. Quem fala é a data, e o motivo é de ordem — não de espaço.
   *
   * A dependência é um percurso: nomeia um curso, desenha a régua e convida ao
   * gesto que a resolve. Pô-la num cartaz que não abre de qualquer maneira é
   * pedir pressa por uma porta trancada por fora — e é a régua a chegar cedo
   * demais, que é a forma mais cara de dizer a coisa certa na hora errada. No
   * dia da estreia a dependência volta a falar, com o nome e a régua, e aí há o
   * que fazer com ela.
   */
  /*
   * ── O CURSO QUE NÃO É DESTA PESSOA MANDA EM TODAS AS OUTRAS RAZÕES ───────
   *
   * Ordem do dono de 02/10/2026, medida em produção no mesmo dia: numa área com
   * 9 grupos de acesso, quem está no grupo de 2 cursos recebia os outros 19
   * cartazes a cores cheias.
   *
   * **Ele é a razão mais de fora que existe**, e por isso é lido primeiro: a
   * data de estreia e o pré-requisito falam do percurso de quem TEM o curso, e
   * dizer «Agendado» ou «pede Fundamentos» sobre um curso que não é da pessoa é
   * mandá-la cumprir o passo errado — ela não tem o que agendar nem o que
   * cumprir. O que ela tem para fazer é outra coisa, e está na página de venda
   * que o tutor declarou.
   *
   * (O servidor já concorda com esta ordem: `trancasDosCursos` só corre sobre os
   * cursos que a pessoa alcança — ver a vitrine, em `routes/aluno.ts`. Aqui
   * fecha-se a mesma porta do lado da tela, porque o `lancamento` chega para
   * todos e o `dist` do aluno pode ser de uma leva mais nova do que a API.)
   */
  const semAcesso = Boolean(curso.bloqueado);
  /*
   * ── O DESTINO DE FORA E O DESTINO DE DENTRO (`0461`) ──────────────────────
   *
   * `curso.oferta` passou a ter duas espécies, e a régua é a FORMA do endereço —
   * não um campo novo. `http(s)://` é a página do TUTOR, noutro domínio;
   * qualquer outra coisa é um caminho DESTE app, a página de promoção que o
   * Vestigo serve quando o tutor não declarou a dele (`course.pagina_publica`).
   *
   * **Por que a forma, e não uma segunda chave:** o servidor escolhe o destino
   * numa expressão só (`c.offerUrl ?? o caminho da promoção`, em
   * `routes/aluno.ts`), de modo que há uma fonte e um leitor. Uma chave «isto é
   * externo» seria um segundo fato a dizer o que o primeiro já diz, e o dia em
   * que os dois discordassem o cartaz abriria aba nova para uma rota interna.
   *
   * **E por que isto importa no desenho:** `target="_blank"` e a `SetaQueSai`
   * estão certos para a página do tutor — a área de membros é um lugar em que a
   * pessoa está, e a venda é uma consulta. Para uma rota deste mesmo app os dois
   * MENTEM: abrir aba nova é perder o lugar por nada, e a seta que sai anuncia
   * uma saída que não acontece. É a `R5-bis` desta casa com o sujeito trocado —
   * nenhum cartaz promete um gesto que não acontece.
   */
  const destinoDeFora = typeof curso.oferta === 'string' && /^https?:\/\//i.test(curso.oferta);
  const lancamento = semAcesso ? null : lerLancamento(curso.lancamento);
  const tranca = semAcesso || lancamento ? null : lerTranca(curso.tranca);
  /*
   * ── O CARTAZ QUE SÓ A BANCADA VÊ (04/11/2026) ─────────────────────────────
   *
   * `curso.soVoceVe` chega apenas na prévia da bancada (`?fonte=rascunho`): a
   * rota do aluno nunca põe o campo, e o rascunho nunca chega lá de qualquer
   * forma — a régua do core deita-o fora antes. `Boolean(...)` e não `!`: o
   * `dist` do aluno e a API podem ser de levas diferentes por uns minutos, e um
   * campo em falta tem de significar «o cartaz é normal», nunca um erro.
   *
   * Ele entra ANTES do `semAcesso` na conta do fechado, e não depois: aqui não
   * há pessoa nenhuma (a prévia pede `{ modo: 'tudo' }`), de modo que as duas
   * espécies não colidem — e se um dia colidirem, «o aluno não vê isto» é a
   * resposta mais de fora que existe, como o acesso é na vitrine dele.
   */
  const soVoceVe = Boolean(curso.soVoceVe);
  /* As duas espécies partilham a caixa: sem `<Link>`, sem levantar e sem barra
     de progresso. O que muda é o carimbo e a linha debaixo do título.

     `semAcesso` NÃO entra aqui, e é a diferença que importa: aquele cartaz
     levanta ao rato e é um link, porque tem para onde levar. Ver o `href` da
     oferta, no fim desta função. */
  const fechado = Boolean(tranca || lancamento || soVoceVe);
  /* O aviso da tranca, aberto pelo clique no cartaz — ver `AvisoDaTranca`, que
     guarda a ordem do dono e a razão de ser um diálogo e não uma navegação. O
     estado é DESTE cartão: numa fileira com dois trancados, um estado partilhado
     faria o segundo clique responder pelo primeiro. */
  const [avisando, setAvisando] = useState(false);

  /* O cartaz é o mesmo objeto nos dois casos — o que muda é o que está por
     baixo dele e se o conjunto é clicável. Escrevê-lo duas vezes faria a arte
     do curso divergir entre o cartão aberto e o trancado no primeiro mês. */
  const cartaz = (
    <div
      ref={animado.caixa}
      /* O cartaz sobe ao passar o mouse, em vez de só crescer. Levantar é o
         gesto de tirar um disco da prateleira; crescer no lugar é o efeito
         padrão de qualquer galeria. Só `transform`, para o compositor resolver
         sozinho — e `motion-reduce` porque a régua vale aqui também.
         `relative` é o que ancora a prévia: ela ocupa esta MESMA caixa, com
         esta MESMA proporção, e por isso nada pula quando ela entra.

         Trancado NÃO levanta: o levantar é a promessa do clique, e aqui não há
         clique. Movimento que promete o que não acontece é pior que nenhum. */
      className={
        fechado
          ? 'relative flex items-end overflow-hidden'
          : 'relative flex items-end overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.02] motion-reduce:transform-none'
      }
      style={{
        aspectRatio: aspectoDaCapa(proporcao),
        /* Sem capa, o fundo continua sendo a marca derivada do id (V7). Com
           capa, o fundo fica de fora e quem desenha é o `<img>` abaixo — a
           caixa é a mesma, o recorte é o mesmo, e o peso deixa de ser. */
        background: curso.capa ? undefined : marca.fundo,
        borderRadius: 'var(--cartao-raio, var(--raio))',
        border: 'var(--cartao-borda, none)',
        boxShadow: 'var(--cartao-sombra, none)',
      }}
    >
      {curso.capa && (
        <CapaEmImagem
          src={curso.capa}
          srcset={curso.capaSrcset}
          sizes={sizesDaCapa(proporcao as never, tamanho)}
          largura={curso.capaLargura}
          altura={curso.capaAltura}
          prioridade="cartaz"
          /* Fechado, a arte perde a cor — e a classe vai NA IMAGEM, não na
             caixa: ver `.vst-arte-fechada`, em `index.css`.

             Sem acesso, o preto e branco é o mesmo e a classe é outra: lá a cor
             VOLTA ao passar o rato, porque aquele cartaz leva à página de venda
             e a arte é o que ela está a vender. Ver `.vst-arte-sem-acesso`. */
          className={fechado ? 'vst-arte-fechada' : semAcesso ? 'vst-arte-sem-acesso' : ''}
        />
      )}
      {/*
       * A prévia — e tudo o que ela NÃO é.
       *
       * Sem `src` não existe elemento nenhum, que é o `preload="none"` do
       * pedido levado ao pé da letra: enquanto ninguém demonstra interesse,
       * este cartaz custa **zero byte** além da imagem que já estava ali.
       *
       * `alt=""` e `aria-hidden` porque ela é decoração pura: o nome do curso
       * está na legenda abaixo, em texto, e o cartaz parado está atrás. Um
       * leitor de tela que anunciasse "imagem" aqui leria a mesma coisa duas
       * vezes.
       *
       * `onError` para o caso de o fornecedor recusar: a prévia some, o cartaz
       * parado continua, e ninguém vê um ícone de imagem quebrada. É a mesma
       * regra do 404 na rota — nada pisca, nada some.
       */}
      {animado.src && (
        <img
          src={animado.src}
          alt=""
          aria-hidden
          className="vst-previa absolute inset-0 h-full w-full object-cover"
          onError={animado.parar}
        />
      )}
      {/*
       * Sem capa, a caixa recebe a MARCA do curso — não o título.
       *
       * O título já está logo abaixo, na legenda que fica sempre. Escrevê-lo
       * aqui dentro punha o mesmo texto duas vezes a 8px de distância, em 16px
       * e 15px: dois títulos quase idênticos que não distinguiam um curso sem
       * capa do outro, porque a caixa dos dois era a mesma. O monograma sobre o
       * gradiente derivado do id distingue; e distingue sem repetir.
       */}
      {!curso.capa && (
        <Monograma
          iniciais={marca.iniciais}
          className={`text-4xl${fechado ? ' vst-arte-fechada' : semAcesso ? ' vst-arte-sem-acesso' : ''}`}
        />
      )}
      {/* O selo vai SOBRE a arte: é a três metros de distância que se pergunta
          "posso abrir?", e a essa distância só o cartaz se lê.

          E desde 24/09/2026 o selo **não está sozinho**: por ordem do dono, o
          cartaz fechado ganhou véu, preto e branco e o cadeado do rato — ver
          `VeuDoCartaz`, que guarda o argumento que isto derrubou. O véu entra
          ANTES dos selos no DOM para ficar por baixo deles: uma chapa sobre a
          arte, e o carimbo sobre a chapa. */}
      {fechado && (
        <VeuDoCartaz
          /*
           * ── AS DUAS ESPÉCIES FECHADAS FALAM DENTRO DA CAPA (14/10/2026) ──
           *
           * *«o texto de quando abre deve ficar na capa … e não abaixo do
           * título para não ocupar espaço»* (24/09). A razão é de fileira: um
           * cartão fechado que ganhava uma linha a mais empurrava o vizinho e
           * desalinhava a prateleira inteira.
           *
           * Naquele dia só a estreia se mudou, e ficou escrito aqui por que a
           * tranca não: *«um link dentro de um véu sobre arte é um alvo que
           * se alcança por acidente, e uma régua de 3px sobre uma fotografia
           * é uma medida que ninguém lê»*.
           *
           * **O dono mediu a prateleira pronta e decidiu o contrário** —
           * *«essa mensagem no rodapé da capa acaba desnivelando as demais
           * capas da prateleira. e o usuário por ter fonte pequena por vezes
           * não percebe o motivo do bloqueio»*. Os dois argumentos de 24/09
           * eram verdadeiros e descreviam a peça de então; o que eles não
           * cobriam é que, com a estreia lá dentro, **o trancado passou a ser
           * o único cartão da fileira com três linhas debaixo do título** — o
           * desnível inteiro da prateleira concentrado numa espécie só.
           *
           * Eles não foram ignorados: foram respondidos. O nome ganhou peso e
           * cresceu em vez de encolher, e a régua sobre a arte é outra pele
           * da mesma peça (5px, arredondada, números em `--texto`). A conta
           * está em `MotivoNoCartaz`, que é onde ela se lê junto da tinta.
           */
          estado={
            soVoceVe ? (
              /* Na bancada a frase da capa é a do estado do CURSO — a estreia
                 quando há data, o rascunho quando não há. Ver `SoVoceVe.tsx`. */
              <SoVoceVeNoCartaz lancamento={lancamento} />
            ) : lancamento ? (
              <EstadoNoCartaz lancamento={lancamento} />
            ) : tranca ? (
              <MotivoNoCartaz tranca={tranca} areaSlug={areaSlug} />
            ) : undefined
          }
        />
      )}
      {/*
       * O véu do cartaz SEM ACESSO — a mesma peça, com o glifo trocado.
       *
       * Trocado porque o fato é outro: o cadeado responde «isto não abre», e
       * este cartaz abre — noutro endereço. A seta que sai é o que o `_blank`
       * do link faz, desenhado. Ver `VeuDoCartaz`, que guarda a razão de o
       * glifo ser um parâmetro, e `SeloSemAcesso`, que guarda a da palavra.
       */}
      {semAcesso && <VeuDoCartaz gesto={destinoDeFora ? <SetaQueSai /> : <SetaQueLeva />} />}
      {tranca && <SeloDeOrdem />}
      {/* UM carimbo por cartaz: «Só você vê» vence o «Agendado», e o porquê
          está no cabeçalho de `SoVoceVe.tsx` — o tutor precisa de saber que o
          aluno não vê este cartão, e a data continua a ler-se na frase do véu. */}
      {soVoceVe ? <SeloSoVoceVe /> : lancamento && <SeloDeLancamento />}
      {semAcesso && <SeloSemAcesso />}
    </div>
  );

  /*
   * O trancado não é `<Link>`, e a razão é a mesma da etapa da trilha: o
   * servidor RECUSA o corpo. Um link aqui seria a tela a oferecer o que a API
   * fecha — o cadeado decorativo ao contrário, que é o que a R5-bis proíbe.
   *
   * E não se confunde com `bloqueado`: ali falta COMPRAR, o cartão continua
   * clicável de propósito e a linha em `--destaque` diz *Você ainda não tem*.
   * Aqui falta FAZER, e quem diz o quê é a frase derivada do fato.
   */
  /*
   * ── MAS ELE RESPONDE AO CLIQUE, DESDE 14/10/2026 ─────────────────────────
   *
   * > *«eu quero que ao clicar em uma capa com dependência de outro curso,
   * > apareça uma mensagem avisando sobre isso, como um modal»*
   *
   * «Não ser um link» tinha virado «não ser nada»: a pessoa tocava no cartaz e
   * **nada acontecia**, e uma tela que não responde não distingue *«isto não
   * abre e eu explico»* de *«isto está quebrado»*. O que muda é a CASCA — um
   * `<button>` em vez de um `<div>` —, e o que ela entrega é a explicação, não
   * o curso: a frase que a capa tem de cortar em quatro linhas, agora no corpo
   * de leitura, com as réguas inteiras e a porta do pré-requisito a um toque.
   * A conta e a gramática do diálogo estão em `AvisoDaTranca`.
   *
   * **O `<Link>` para `/c/:id` continua proibido** — o 403 é o mesmo 403 —, e
   * levar direto ao pré-requisito é o atalho que apaga a explicação, que esta
   * casa já pagou duas vezes. Primeiro o porquê; a porta vem depois dele.
   *
   * ## E o cartaz AGENDADO continua inerte, de propósito
   *
   * Porque ele não tem segunda camada nenhuma para dar: a frase da estreia cabe
   * inteira na capa (uma linha), e «ainda não é hora» **não tem gesto do outro
   * lado** — está escrito em `Lancamento.tsx`, e é a identidade daquela espécie.
   * Um diálogo que repetisse «Abre em 12 de março» e oferecesse só «Fechar»
   * seria um clique a mais para ler o que já estava lido.
   */
  if (fechado) {
    const pele = {
      'data-vst-regiao': 'capa',
      'data-vst-rotulo': curso.title,
      'data-vst-curso': curso.id,
      'data-vst-cartao': `curso:${curso.id}`,
      /* `group` é o que dá hover ao cadeado: o cartão fechado não leva ao curso,
         e sem um ancestral nomeado o `group-hover` do véu nunca dispara. */
      className: 'group shrink-0 snap-start',
      style: { width: larguraDaCapa(proporcao as never, tamanho) },
    } as const;

    const miolo = (
      <>
        {cartaz}
        <p className="pt-2.5 text-[15px] font-medium leading-snug">{curso.title}</p>
        {/* A barra de progresso DESTE curso sai quando há tranca: duas barras a
            8px uma da outra, uma sobre o curso e outra sobre a dependência,
            seriam duas medidas parecidas a medir coisas diferentes — e a régua
            da tranca é a que responde à pergunta de quem está a olhar.

            Com data de estreia ela sai pela razão inversa e mais forte: o
            progresso da pessoa não tem nada a ver com o dia em que o curso
            abre, e uma barra ali seria a tela a insinuar que estudar adianta o
            calendário. */}
        {/*
         * ── ABAIXO DO TÍTULO FICA UMA LINHA, E É A DO VIZINHO (14/10/2026) ─
         *
         * Com as DUAS espécies fechadas a falar dentro da capa, o que sobra
         * aqui é a legenda de sempre — «8 aulas», a MESMA linha do cartão
         * aberto, no MESMO elemento e com as MESMAS classes. É isso que faz o
         * cartão fechado ter exactamente a altura do vizinho, que é a razão
         * que o dono deu nos dois dias em que mandou mexer nisto.
         *
         * **E o invólucro `pt-1.5` saiu.** Ele existia porque o motivo era um
         * bloco e a legenda não; media-se em 6px, e era ele que fazia o cartão
         * agendado pedir 379,02px onde o aberto pedia 373,02 — está escrito em
         * `CapaDeTrilha`, que se recusou a herdá-lo. Sem o motivo aqui, o
         * invólucro deixou de ter dono, e com ele vai-se o último desvio que a
         * espécie fechada acrescentava à fileira.
         */}
        <p className="text-xs tabular-nums" style={{ color: 'var(--texto-suave)' }}>
          {/* A MESMA legenda do cartão aberto, e é ela que faz o cartão fechado
              ter exactamente a altura do vizinho — o invariante que o comentário
              acima protege. Com a carga ligada os dois ganham o mesmo segmento,
              e a igualdade sobrevive. */}
          {legendaDoCurso(curso)}
        </p>
      </>
    );

    if (tranca) {
      return (
        <>
          <button
            {...pele}
            type="button"
            data-vst-tranca="cartao"
            /* O que a casca passou a prometer, dito à máquina: isto abre um
               diálogo. Sem `aria-label`: o nome acessível sai do CONTEÚDO —
               «Trancado · Para abrir · a frase · o título · a legenda» —, e
               um rótulo por cima dele apagaria a frase e as réguas de quem
               ouve a tela, que é precisamente quem mais precisa delas. */
            aria-haspopup="dialog"
            /* `text-left` porque `<button>` centra o texto por herança, e sem
               ele o título e a legenda deste cartão nasciam centrados ao lado
               dos vizinhos alinhados à esquerda. `cursor-pointer` porque o
               preflight do Tailwind v4 dá `cursor: default` ao botão. */
            className="group shrink-0 cursor-pointer snap-start text-left"
            onClick={() => setAvisando(true)}
          >
            {miolo}
          </button>
          {avisando && (
            <AvisoDaTranca
              especie="curso"
              tranca={tranca}
              areaSlug={areaSlug}
              aoFechar={() => setAvisando(false)}
            />
          )}
        </>
      );
    }

    return (
      <div {...pele} data-vst-lancamento="cartao">
        {miolo}
      </div>
    );
  }

  /*
   * ── O CLIQUE DO CARTAZ SEM ACESSO VAI À PÁGINA DE VENDA (02/10/2026) ──────
   *
   * Ordem do dono: *«ao clicar no curso, ele não direciona para o link
   * cadastrado no curso para qual ele deve ir para comprar, só abre uma tela
   * preta; deveria ter um redirecionamento para o link cadastrado»*.
   *
   * Até aqui este cartaz era um `<Link>` para `/c/:id`. Aquela página pedia o
   * curso, levava `403 curso_nao_comprado`, e **só então** desenhava o convite
   * com o endereço — uma tela intermédia e uma ida à rede para chegar onde o
   * dono já tinha escrito que queria mandar. (E, até hoje, a tela intermédia
   * desenhava-se sem casca de tema: texto preto sobre o fundo preto da área, que
   * é a «tela preta» da ordem. Ver `SemAcessoAoCurso`, em `App.tsx`.)
   *
   * ## `<a>` e não `<Link>`, e por que o endereço não passa pelo roteador
   *
   * Porque ele é de outro sítio: `offerUrl` é a página de venda do tutor — um
   * domínio dele, uma LP do Vestigo, um checkout. Dá-lo ao roteador faria o
   * React Router procurar uma rota do app do aluno para `https://…`.
   *
   * ## Nova aba, de propósito
   *
   * A área de membros é um lugar em que a pessoa está, e a página de venda é
   * uma consulta. Trocar uma pela outra faz quem voltar aterrar na entrada da
   * área — ou no seletor —, e perder a prateleira onde estava. `rel="noopener"`
   * porque abrir aba nomeada dá ao destino acesso a `window.opener`, e este
   * destino é texto que o tutor escreveu num campo.
   *
   * ## E sem `oferta`, continua a ser o `<Link>` de sempre
   *
   * O curso sem página de venda declarada não chega à vitrine (a rota filtra-o:
   * ver `routes/aluno.ts`), mas a prévia do Estúdio monta estes objetos à mão e
   * um `href` vazio seria um link para a própria página. Lá o `/c/:id` responde,
   * e responde com a tela que diz que o curso não está no acesso desta pessoa.
   */
  /*
   * ── O DESTINO DE DENTRO É UM `<Link>`, E SEM ABA NOVA (`0461`) ────────────
   *
   * A página de promoção que o Vestigo serve é uma rota deste app. Mandá-la por
   * `<a target="_blank">` abriria uma aba para o mesmo SPA — recarregando o
   * bundle inteiro, perdendo a prateleira, e com um glifo de saída que não sai
   * de sítio nenhum. Ver `destinoDeFora`, acima, para a régua e o porquê.
   *
   * O carimbo e o véu ficam IGUAIS nos dois casos, de propósito: o que a pessoa
   * lê é «este não é meu», e para onde isso leva é decisão do tutor — não estado
   * do cartão. O que muda é o glifo do rato e a forma do elo.
   */
  if (semAcesso && curso.oferta && !destinoDeFora) {
    return (
      <Link
        to={caminhoDaArea(areaSlug, curso.oferta)}
        data-vst-regiao="capa"
        data-vst-rotulo={curso.title}
        data-vst-curso={curso.id}
        data-vst-cartao={`curso:${curso.id}`}
        data-vst-sem-acesso="cartao"
        /* O rótulo diz as duas coisas, como no de fora — e não promete aba
           nova, que aqui não acontece. */
        aria-label={`${rotuloDoCartao(curso)} — abre a página deste curso`}
        className="group shrink-0 snap-start"
        style={{ width: larguraDaCapa(proporcao as never, tamanho) }}
      >
        {cartaz}
        {/* Nem barra, nem legenda de aulas — as mesmas duas razões do cartão de
            fora, logo abaixo. */}
        <p className="pt-2.5 text-[15px] font-medium leading-snug">{curso.title}</p>
        <p className="pt-1.5 text-xs" style={{ color: 'var(--destaque)' }}>
          {t('vitrine.voceAindaNaoTem')}
        </p>
      </Link>
    );
  }

  if (semAcesso && curso.oferta) {
    return (
      <a
        href={curso.oferta}
        target="_blank"
        rel="noopener noreferrer"
        data-vst-regiao="capa"
        data-vst-rotulo={curso.title}
        data-vst-curso={curso.id}
        data-vst-cartao={`curso:${curso.id}`}
        data-vst-sem-acesso="cartao"
        /* O rótulo diz as duas coisas que quem ouve a tela precisa de saber
           antes de a aba mudar: que o curso não é dela, e para onde isto vai. */
        aria-label={`${rotuloDoCartao(curso)} — abre a página de venda`}
        className="group shrink-0 snap-start"
        style={{ width: larguraDaCapa(proporcao as never, tamanho) }}
      >
        {cartaz}
        {/* Nem barra, nem legenda de aulas: ver as duas razões junto do cartão
            fechado, acima. O que fica é a linha que o dono escreveu em 18/09. */}
        <p className="pt-2.5 text-[15px] font-medium leading-snug">{curso.title}</p>
        <p className="pt-1.5 text-xs" style={{ color: 'var(--destaque)' }}>
          {t('vitrine.voceAindaNaoTem')}
        </p>
      </a>
    );
  }

  return (
    <Link
      to={caminhoDaArea(areaSlug, `/c/${curso.id}`)}
      /* Os quatro no mesmo elemento: o foco do teclado precisa disparar o
       * mesmo que o mouse, senão quem navega por `Tab` nunca vê o recurso. */
      onMouseEnter={animado.ligar}
      onMouseLeave={animado.parar}
      onFocus={animado.ligar}
      onBlur={animado.parar}
      /* `vst-curso` faz o clique NAVEGAR o Sumário em vez de abrir a marginália:
       * é a metade que fecha o laço entre as duas colunas. */
      data-vst-regiao="capa"
      data-vst-rotulo={curso.title}
      data-vst-curso={curso.id}
      data-vst-cartao={`curso:${curso.id}`}
      /* O cadeado é o convite, não a recusa: o clique leva à página de venda que
       * o dono declarou, e é para isso que este card está na vitrine. */
      /*
       * ── O RÓTULO DIZIA MENOS DO QUE A TELA (0439) ─────────────────────────
       *
       * `aria-label` SUBSTITUI o conteúdo do elemento: com só o título aqui,
       * «48 aulas» já se perdia para quem usa leitor de tela — antes desta obra
       * e sem ninguém notar. A carga entra junto com o conserto.
       *
       * Abreviado no ecrã, por extenso no rótulo: `10 h 42 min` é lido em voz
       * alta por vários leitores como «dez h quarenta e dois min».
       */
      aria-label={rotuloDoCartao(curso)}
      className="group shrink-0 snap-start"
      style={{ width: larguraDaCapa(proporcao as never, tamanho) }}
    >
      {cartaz}
      <Barra percent={curso.progresso?.percent ?? 0} />
      {/*
       * O título fica, e não vai para dentro da arte.
       *
       * A referência não escreve nada embaixo da capa — e funciona, porque as
       * capas dela são desenhadas com o nome dentro. As nossas são o arquivo que
       * cada cliente subiu: pode ser uma foto sem uma letra. Tirar a legenda
       * apostaria a navegação da área inteira numa condição que não controlamos,
       * e o leitor de tela perderia junto.
       *
       * O que muda é o peso: agora ela é legenda de um cartaz grande, não o
       * conteúdo do cartão.
       */}
      <p className="pt-2.5 text-[15px] font-medium leading-snug">{curso.title}</p>
      {/*
       * A linha embaixo do card diz o que ele É para esta pessoa.
       *
       * "Você ainda não tem" em vez de um cadeado mudo: o cadeado comunica
       * proibição, e este card está aqui porque o dono QUER que a pessoa clique.
       */}
      <p
        className="text-xs"
        style={{ color: curso.bloqueado ? 'var(--destaque)' : 'var(--texto-suave)' }}
      >
        {/*
         * O cartão BLOQUEADO não ganha a carga, e é firme.
         *
         * Esta linha responde a outra pergunta — posse, não tamanho —, está em
         * `var(--destaque)`, que é a cor de um alerta, e pendurar «· 10 h 42 min»
         * nela anunciaria o tamanho do que a pessoa não tem. Quem quer vender o
         * tamanho tem a página de venda para onde este cartão já leva.
         */}
        {curso.bloqueado ? t('vitrine.vocAindaNaoTem') : legendaDoCurso(curso)}
      </p>
    </Link>
  );
}

/** Uma linha de "Continue de onde parou", como o servidor a manda. */
type AulaParaRetomar = NonNullable<DadosDaVitrine['continuando']>[number];

/**
 * A largura do ladrilho de retomada — e por que ela **não** lê `capaTamanho`.
 *
 * ## O que estava errado
 *
 * O cartão usava `larguraDaCapa('16/9', dados.capaTamanho)`. Numa área com o
 * cartaz em `grande`, isso resolve para `clamp(274px, 30vw, 528px)`: dois
 * cartões de 528×297 mais legenda ocupavam **83% da largura** e ~373px de
 * altura para dizer duas linhas de texto. É a desproporção que o dono
 * fotografou.
 *
 * A causa não é o número, é o **acoplamento**. `capaTamanho` está documentado em
 * `largura-da-capa.ts` como *"o cartaz que o tutor escolheu"* — é o controle da
 * prateleira de **cursos**, onde a arte é o conteúdo do cartão. Aqui a arte não
 * é o conteúdo: nenhuma aula publicada desta área tem capa (medido: 30 de 30). O
 * tutor que pediu cartazes maiores não estava pedindo isto.
 *
 * ## Por que aqui, e não no core
 *
 * `larguraDaCapa` mora em `@vestigo/core` porque **duas pontas precisam do mesmo
 * número**: o aluno para desenhar e o painel para nomear a opção em pixels na
 * prévia do Estúdio. Este ladrilho não tem controle no painel — ninguém escolhe
 * o tamanho dele —, então subir a conta ao core criaria um contrato entre uma
 * ponta e ela mesma.
 *
 * ## As três larguras
 *
 * | janela | vão útil (`px-6`/`sm:px-8`) | ladrilho | inteiros | o seguinte |
 * |---|---|---|---|---|
 * | 390 | 342 | 300 (piso) | 1 | espia 42px |
 * | 900 | 836 | 300 (piso) | 2 | espia 204px |
 * | 1280 | 1216 | 358 (28vw) | 3 | espia 93px |
 *
 * O piso de 300 não é folga: abaixo dele a coluna de texto (300 − 24 de recuo −
 * 96 da miniatura − 12 de vão = 168px) deixa de caber um título de duas linhas a
 * 15px, e o `line-clamp-2` passaria a cortar títulos normais.
 */
/**
 * O ladrilho preenche a CÉLULA da grade — ordem do dono de 24/09/2026.
 *
 * Era `clamp(300px, 28vw, 416px)`, e daí vinha o defeito: com largura própria, o
 * número de cartões por fileira era consequência do tamanho da janela (quatro a
 * 1920, três a 1440), e nenhum teto conseguia prometer «uma fileira». Quem manda
 * agora são as colunas da grade (`xl:grid-cols-5`), e o cartão ocupa o que lhe
 * couber — 358px a 1920, 230px a 1280.
 *
 * A constante fica exportada porque é ela que as cercas usam para provar que a
 * largura do ladrilho **não** é a do cartaz: o `capaTamanho` que o tutor escolhe
 * governa a capa do curso, e nunca esta faixa.
 */
export const LARGURA_DO_LADRILHO_DE_RETOMADA = '100%';

/**
 * O que o ladrilho diz — e por que as duas promessas têm gramáticas diferentes.
 *
 * A prateleira tem dois tipos de item, e eles respondem a perguntas distintas:
 *
 * - **`retomar`** — "você estava aqui". O que vale é **o ponto**: `4:32 de 12
 *   min` devolve o lugar, e o trilho embaixo do ladrilho o desenha. Um
 *   percentual sozinho (`2% assistido`, que era o que a tela dizia) informa
 *   menos do que parece: 2% de quê, e onde é isso?
 * - **`seguir`** — "a próxima é esta". Não há ponto, porque não houve começo.
 *   Escrever `0% assistido` seria anunciar um fracasso onde houve **conclusão**
 *   da aula anterior. A palavra `Próxima` abre a linha, e o que resta é a
 *   pergunta de quem decide agora: dá tempo?
 *
 * As duas linhas começam com **o tipo** (`Vídeo`, `Texto`, `Avaliação`), que é o
 * que o cartão não dizia e o dono pediu: uma leitura de 3 min e um vídeo de 3
 * min não são o mesmo compromisso, e a decisão acontece antes do clique.
 *
 * ## A cascata de `retomar`, e o que cada degrau significa
 *
 * `retomarDe` **não é uma medição, é uma decisão**: `posicaoParaRetomar`
 * (`core/area/progresso.ts`) devolve `null` abaixo de 30s e acima de 95% da
 * duração — cedo demais para valer a pena, ou tarde demais para não ser o fim.
 * Então a frase desce um degrau de cada vez, e nunca inventa o degrau de cima:
 *
 * 1. há ponto e há duração → `Vídeo · 4:32 de 12 min`
 * 2. há progresso, mas não ponto → `Vídeo · 12 min · 4% assistido`
 * 3. nem isso → `Vídeo · 12 min · você começou`
 *
 * `motivo` ausente lê-se como `retomar`: é o que a prévia do Estúdio manda (ela
 * não tem pessoa) e é o que a vitrine mostrava antes de `seguir` existir.
 */
export interface FichaDaRetomada {
  /** `retomar` | `seguir`. **A forma do ladrilho sai daqui**, não só o texto. */
  estado: 'retomar' | 'seguir';
  /** A linha de baixo: `Vídeo · 4:32 de 12 min` ou `Próxima · Vídeo · 12 min`. */
  linha: string;
  /**
   * O verbo, para o `aria-label`.
   *
   * Ele existe porque a diferença entre os dois estados é **visual** — painel
   * cheio com trilho contra contorno vazio —, e forma não é anunciada. Quem lê
   * por áudio recebe a mesma distinção em palavra, no único lugar onde ela cabe
   * sem virar uma quarta linha na tela de quem enxerga.
   */
  chamada: string;
}

export function fichaDaRetomada(
  aula: {
    motivo?: 'retomar' | 'seguir';
    kind?: string | null;
    percent: number;
    duracao: number | null;
    retomarDe?: number | null;
  },
  t: Tradutor,
): FichaDaRetomada {
  const tipo = rotuloDoTipo(aula.kind);
  const inteira = duracaoLegivel(aula.duracao);

  if (aula.motivo === 'seguir') {
    return {
      estado: 'seguir',
      chamada: 'Começar',
      /* `filter(Boolean)` e não interpolação: texto e PDF não têm duração, e a
         linha encolhe em vez de escrever ` · ` no vazio. É a mesma trava de C5
         que o `join` de `fichaDaAula` em `/minhas-aulas` já usa. */
      linha: ['Próxima', tipo, inteira].filter(Boolean).join(' · '),
    };
  }

  const ponto = posicaoLegivel(aula.retomarDe);
  const onde =
    ponto && inteira
      ? `${ponto} de ${inteira}`
      : [
          inteira,
          aula.percent > 0
            ? t('vitrine.percentAssistido', { percent: aula.percent })
            : t('vitrine.voceComecou'),
        ]
          .filter(Boolean)
          .join(' · ');

  return {
    estado: 'retomar',
    chamada: 'Continuar',
    linha: [tipo, onde].filter(Boolean).join(' · '),
  };
}

/**
 * O ladrilho de "Continue de onde parou".
 *
 * ## O que ele era, e por que não era elegante
 *
 * Um **cartaz**: caixa 16/9 grande com o monograma do curso centrado num
 * gradiente, e três linhas de legenda embaixo. O dono fotografou dois deles,
 * `AC` e `OH`, ocupando 83% da largura da tela.
 *
 * O diagnóstico não é "está feio", é **desproporção entre o que ocupa e o que
 * informa**. A caixa de arte levava ~70% da área do cartão, e o que havia nela
 * era um monograma — uma peça desenhada para outro trabalho. Na prateleira de
 * cursos, o monograma distingue *um cartaz pequeno entre muitos*; ali ele é a
 * segunda coisa que se olha e nunca é a informação. Aqui, com dois itens e meia
 * tela cada, ele virou a primeira coisa que se olha, e continua não sendo a
 * informação: `AC` não diz que aula é, nem se é vídeo, nem quanto dura, nem onde
 * a pessoa parou.
 *
 * E o caso sem capa **é a regra, não a exceção**: medido em produção em
 * 09/08/2026, das 30 aulas da área `Comunidade Openclaw` **nenhuma publicada tem
 * capa** (a única que tem está em rascunho). Um desenho que só fica bom quando há
 * arte é um desenho que este produto não pode usar.
 *
 * ## O que mudou: o cartaz vira ladrilho, e a marca vira selo
 *
 * A composição inverte. A miniatura desce a 96px (124 acima de `sm`) e passa a
 * ser um **selo à esquerda do texto** — que é o tamanho em que um monograma faz
 * o trabalho para o qual foi desenhado: identificar de relance, sem competir. O
 * texto ganha a largura, e é ele que diz o que o cartão precisa dizer.
 *
 * Nada de tratamento novo: é a **mesma** `marcaDoCurso` e o **mesmo**
 * `Monograma`, e é a **mesma proporção de linha de aula** que `/minhas-aulas`
 * (`ItemDaFila`, commit `5d96b13`) já resolveu — miniatura 16/9 à esquerda,
 * curso em miúdo, título, ficha. Aqui a miniatura é menor porque o ladrilho vive
 * numa prateleira que rola, e não numa coluna de 1120px.
 *
 * ## Os dois estados, na forma e não só na prosa
 *
 * É a lição de `FilaEAcervo`: a fila **esvazia** e o acervo **fica**, e a
 * diferença saiu dos quatro parágrafos e entrou na forma. Aqui as duas promessas
 * também são distintas, e por isso as duas formas são:
 *
 * | | `retomar` | `seguir` |
 * |---|---|---|
 * | promessa | "você estava em 4:32" | "a próxima é esta" |
 * | corpo | **painel cheio** (`--superficie`) | **contorno**, quase sem preenchimento |
 * | base | **trilho de progresso**, sempre | **nenhum** |
 * | linha | `Vídeo · 4:32 de 12 min` | `Próxima · Vídeo · 12 min` |
 * | verbo (áudio) | `Continuar` | `Começar` |
 *
 * Massa contra contorno se lê a três metros, que é o teste que `FilaEAcervo`
 * usa. E os dois lados são **verdadeiros**: `retomar` tem substância atrás dele
 * — há progresso gravado —, `seguir` não tem nada ainda, e a forma mais leve
 * afirma isso em vez de fingir simetria.
 *
 * O trilho do `retomar` desenha a **pista inteira**, mesmo a 0%: assim "este
 * estado tem trilho" é uma regra, e não uma coincidência de haver progresso.
 * Uma pista vazia diz "há uma medida aqui, e você está no começo dela", que é o
 * que aconteceu.
 *
 * ## O que ele NÃO passou a dizer, e por quê
 *
 * **O módulo.** A prateleira manda **uma aula por curso** (`retomada.ts`,
 * `TETO_DE_RETOMADAS = 5`), então o nome do curso já localiza a aula sozinho — é
 * para isso que ele está na primeira linha. O módulo seria uma quarta linha num
 * ladrilho de ~112px, custaria um campo novo na rota do aluno, e faria deste o
 * **único** lugar do produto onde uma aula carrega o módulo: nem `ListaDaSala`
 * (que já está dentro do curso) nem `/minhas-aulas` o mostram.
 *
 * ## A cascata da imagem, que não mudou
 *
 * Capa da aula → marca do curso. **Nunca** a capa do curso no meio, e o motivo
 * continua valendo: são objetos de formato diferente (o pôster é `3/4` ou `2/3`
 * por declaração da área; esta caixa é 16/9, e encaixar um no outro com
 * `object-cover` corta 56% da altura, centrado, que é onde um pôster desenhado
 * põe o nome), e duas faixas passariam a mostrar a mesma arte levando a lugares
 * diferentes — o espírito de R7.
 *
 * ## Texto sobre imagem: nenhum
 *
 * Todo o texto vive fora da miniatura, sobre `--superficie`. Calculado no tema
 * padrão da área: `--texto-suave` (`#a1a1aa`) sobre `--superficie` (`#16161d`)
 * dá **7,01:1**, contra os 4,5:1 que C4 pede para texto de até 18px — e sem
 * depender de qual foto o dono subiu, que é a conta que a etiqueta do herói
 * perdeu por 2,11:1.
 */
function CartaoDeRetomada({ aula, areaSlug }: { aula: AulaParaRetomar; areaSlug: string }) {
  const t = useT();
  const marca = marcaDoCurso(aula.courseId, aula.curso);
  const ficha = fichaDaRetomada(aula, t);
  const retomando = ficha.estado === 'retomar';
  /*
   * ── COMO É QUE SE «CONTINUA» UM CURSO TRANCADO? (24/09/2026) ──────────────
   *
   * Pelo caminho mais comum de todos: o tutor cria o pré-requisito **depois**,
   * sobre gente que já começou. Com a vitrine e a trilha já a desenhar o
   * cadeado, este ladrilho era o último sítio da página que ainda convidava ao
   * clique que o servidor recusa.
   *
   * **A linha fica, e o progresso dela fica com ela.** O servidor manda o FATO e
   * não a decisão de esconder — esconder seria apagar o que a pessoa fez.
   */
  const tranca = lerTranca(aula.tranca);

  const miolo = (
    <>
      <div className="flex flex-1 items-start gap-3 p-3">
        <div
          /* `aspect-video` e não uma altura: a miniatura tem a proporção do
             vídeo, e é a mesma caixa de `CapaDaAula` em `/minhas-aulas`. */
          className="relative aspect-video w-[88px] shrink-0 overflow-hidden 2xl:w-[112px]"
          style={{
            /* Sem capa, a caixa recebe a marca do curso — o mesmo gradiente do
               cartaz sem capa e do cartão de `/minhas-aulas`. Com capa, o fundo
               fica de fora e quem desenha é o `<img>`. */
            background: aula.capa ? undefined : marca.fundo,
            borderRadius: 'calc(var(--raio) * 0.5)',
          }}
        >
          {aula.capa ? (
            <CapaEmImagem
              src={aula.capa}
              srcset={aula.capaSrcset}
              /* Fixo, porque a miniatura é fixa: dois degraus, e não a `clamp`
                 do cartaz. Um `sizes` maior do que a caixa faz o navegador
                 baixar o derivado grande para desenhar 96px. */
              sizes="(min-width: 640px) 124px, 96px"
              largura={aula.capaLargura}
              altura={aula.capaAltura}
              prioridade="cartaz"
            />
          ) : (
            /* `text-base`, e não `text-3xl`: aqui o monograma é um SELO ao lado
               do texto, e não o conteúdo do cartão. É a mesma peça no tamanho em
               que ela identifica sem reivindicar a tela. */
            <Monograma iniciais={marca.iniciais} className="text-base sm:text-lg" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          {/* O curso primeiro, em miúdo: é o que localiza a aula. Sem ele,
              "Backup e restauração do estado" não diz de que curso se fala. */}
          <p
            className="flex items-center gap-1.5 truncate text-xs"
            style={{ color: 'var(--texto-suave)' }}
          >
            {tranca && <Cadeado tamanho={11} />}
            {aula.curso}
          </p>
          <p className="line-clamp-2 pt-0.5 text-[15px] font-medium leading-snug">{aula.title}</p>
          {/*
           * A linha de baixo diz «4:32 de 12 min» — onde ela parou. Com tranca,
           * esse número deixou de ser a informação: ele responde «onde eu
           * estava» a quem está a perguntar «por que não abre». O motivo toma o
           * lugar dele, e não se acrescenta a ele: as duas linhas juntas fariam
           * um ladrilho de 300px com cinco linhas de texto.
           */}
          {tranca ? (
            <div className="pt-1">
              <MotivoDaTranca tranca={tranca} areaSlug={areaSlug} compacto reguas={false} />
            </div>
          ) : (
            <p className="truncate pt-1 text-xs" style={{ color: 'var(--texto-suave)' }}>
              {ficha.linha}
            </p>
          )}
        </div>
      </div>

      {/* O trilho fica mesmo com tranca: o progresso é dela. E é por ele existir
          aqui que o motivo vem SEM régua — duas barras a 8px uma da outra, uma
          sobre a aula e outra sobre a dependência, não se leem. */}
      {retomando && <Trilho percent={aula.percent} />}
    </>
  );

  const pele = {
    style: {
      width: LARGURA_DO_LADRILHO_DE_RETOMADA,
      borderRadius: 'var(--raio)',
      /*
       * Cheio contra vazio — a diferença de massa entre os dois estados.
       *
       * O contorno ainda recebe `--superficie` a 40% em vez de nada: sobre
       * `--fundo` o ladrilho vazio precisa continuar sendo uma superfície
       * clicável, e um retângulo puramente contornado no meio de uma
       * prateleira lê-se como espaço reservado — o "esqueleto" que ainda vai
       * carregar.
       */
      background: retomando
        ? 'var(--superficie)'
        : 'color-mix(in srgb, var(--superficie) 40%, transparent)',
      /* A borda existe nos DOIS, e num deles é transparente. Sem isto os
         ladrilhos de cada estado teriam 2px de diferença de altura e de
         largura, e a prateleira ficaria irregular por um detalhe que ninguém
         consegue nomear ao olhar. */
      border: retomando
        ? '1px solid transparent'
        : '1px solid color-mix(in srgb, var(--texto-suave) 24%, transparent)',
    },
  };

  /* Trancado não é link, e não levanta ao passar o mouse: o levantar é a
     promessa do clique. Mesma decisão do cartaz e da etapa da trilha. */
  if (tranca) {
    return (
      <div
        data-vst-tranca="retomada"
        className="flex shrink-0 snap-start flex-col overflow-hidden"
        style={pele.style}
      >
        {miolo}
      </div>
    );
  }

  return (
    <Link
      to={caminhoDaArea(areaSlug, `/aula/${aula.lessonId}`)}
      /*
       * O verbo vem primeiro, e é a única coisa que o `aria-label` acrescenta.
       *
       * A distinção entre os dois estados é de forma, e forma não é anunciada.
       * O resto do rótulo repete a ordem em que o ladrilho se lê, para que a
       * leitura por áudio e a leitura por olho contem a mesma história.
       */
      aria-label={`${ficha.chamada}: ${aula.title} — ${aula.curso} · ${ficha.linha}`}
      className="group flex shrink-0 snap-start flex-col overflow-hidden transition-transform duration-300 ease-out hover:-translate-y-1 motion-reduce:transform-none"
      style={pele.style}
    >
      {miolo}
    </Link>
  );
}

/**
 * O trilho na base do ladrilho de `retomar` — a pista inteira, sempre.
 *
 * Diferente da `Barra` do cartaz de curso, que **some** a 0% porque uma barra
 * zerada em toda a prateleira de cursos vira ruído. Aqui é o contrário: o trilho
 * é metade da distinção entre os dois estados, e um `retomar` a 0% sem trilho
 * seria um ladrilho com a forma do estado errado. A pista vazia informa — há uma
 * medida, e você está no começo dela.
 *
 * `Math.min/max` não é paranoia: `percent` chega do servidor, e um valor fora da
 * faixa desenharia o preenchimento para fora do arredondamento do ladrilho.
 */
function Trilho({ percent }: { percent: number }) {
  const p = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  return (
    <div
      /* A marca é para a trava, e não para o Estúdio: a presença do trilho é
         metade da distinção entre os dois estados, e `happy-dom` não faz layout
         — sem um alvo nomeado, "só `retomar` tem trilho" não teria como ser
         verificado sem comparar strings de estilo. */
      data-vst-trilho=""
      className="h-1 w-full shrink-0"
      style={{ background: 'color-mix(in srgb, var(--texto-suave) 22%, transparent)' }}
    >
      <div className="h-full" style={{ width: `${p}%`, background: 'var(--destaque)' }} />
    </div>
  );
}

/** Sem barra quando não há progresso: uma barra a zero em todo card vira ruído. */
function Barra({ percent }: { percent: number }) {
  if (percent <= 0) return null;
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden" style={{ background: 'var(--superficie)' }}>
      <div className="h-full" style={{ width: `${percent}%`, background: 'var(--destaque)' }} />
    </div>
  );
}

/**
 * ── O CARTÃO DA MINHA TRILHA, NA FAIXA DA RETOMADA ─────────────────────────
 *
 * Escolha do dono de 30/09/2026: a vitrine ganha **um cartão** quando há trilha em
 * curso. Ele mora na faixa «Continue de onde parou» e não numa seção nova, porque
 * a pergunta que ele responde é a mesma daquela faixa — «e agora, o quê?» — e
 * porque uma seção nova obrigaria a tocar o editor de vitrine do tutor para dizer,
 * no fim, a mesma coisa.
 *
 * ## Por que ele NÃO é um `CartaoDeRetomada` com outro texto
 *
 * Porque aquele cartão é uma CAPA: ele mostra a imagem da aula e a barra de
 * progresso dela. Uma trilha não tem capa — ela tem um caminho —, e dar-lhe a capa
 * da próxima aula faria o aluno ler «continue nesta aula» onde a frase é «continue
 * nesta trilha, e a próxima é esta».
 *
 * Então ele é texto: a conta do caminho, o título do que ele vai conseguir fazer, e
 * a próxima etapa NOMEADA. É a única coisa que distingue uma trilha de uma
 * prateleira, e é a mesma lei que a página das trilhas (`TrilhasDaArea`) escreve
 * para a trilha do tutor: *«a prateleira mostra o que existe; a trilha afirma uma ordem, e a
 * afirmação só vale se ela responder “e agora, o quê?”»*.
 *
 * ## E o possessivo é obrigatório
 *
 * «Trilhas», sem ele, é a seção das trilhas do TUTOR, que pode estar na mesma
 * página, a dois blocos de distância. O rótulo aqui diz **«a sua trilha»**.
 */
function CartaoDaMinhaTrilha({
  trilha,
  areaSlug,
}: {
  trilha: NonNullable<DadosDaVitrine['minhaTrilha']>;
  areaSlug: string;
}) {
  const t = useT();
  /* Sem próxima aula viva não há para onde levar: o cartão vira a porta de
     «Minhas aulas», onde ele vê a trilha inteira e o que lhe aconteceu. */
  const destino = trilha.proxima
    ? caminhoDaArea(areaSlug, `/aula/${trilha.proxima.aulaId}`)
    : caminhoDaArea(areaSlug, '/minhas-aulas');
  return (
    <Link to={destino} className="vst-cartao-da-trilha" data-vst-cartao-da-minha-trilha="">
      <span className="vst-rubrica">{t('trilha.aSuaTrilha')}</span>
      <span className="vst-cartao-da-trilha-titulo">{trilha.titulo}</span>
      <span className="vst-cartao-da-trilha-conta">
        {trilha.feitas} de {trilha.total} aulas
      </span>
      {/* A barra é a conta desenhada, e não um segundo número: ela sai de
          `feitas/total` e não existe sem eles. */}
      <span className="vst-cartao-da-trilha-barra" aria-hidden>
        <span style={{ width: `${Math.round((trilha.feitas / trilha.total) * 100)}%` }} />
      </span>
      {trilha.proxima ? (
        <span className="vst-cartao-da-trilha-proxima">
          {t('vitrine.aSeguir', { titulo: trilha.proxima.titulo })}
        </span>
      ) : (
        /* Todas as etapas que restam perderam a aula. Dizer isso é melhor do que
           um cartão que promete uma próxima e não tem nenhuma. */
        <span className="vst-cartao-da-trilha-proxima">{t('vitrine.verATrilhaEmMinhasAulas')}</span>
      )}
    </Link>
  );
}
